import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  TextField,
  Container,
  Grid,
  Typography,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Autocomplete,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Modal,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  Radio,
  makeStyles,
  styled,
  Tooltip,
  IconButton,
} from "@mui/material";
import { attentionColors, mobiliTheme } from "../../../../../themes/mobiliTheme";
import { Add, Check, CheckBox, Delete, PersonSearch } from "@mui/icons-material";
import Controls from "../../../../../atoms/Controls";
import TextArea from "../../../../../atoms/TextArea";
import { getKvnrError } from "../../../../../utils/kvnrValidator";
import { resolveSourcedValue } from "../rxFieldSource";
import { observer } from "mobx-react";
import { initialValues } from "../../../../pages/Patient/initialValues";
import useStore from "../../../../../helpers/useStore";
import {
  defaultDiagnosisGroup,
  diagnosisGroupsForRx,
  findDiagnosisGroup,
} from "../../../../../helpers/diagnosisGroupSelection";
import {
  useMasterTables,
  resolveTarifGroupForRxType,
  getTarifGroupsForTherapyType,
  getTherapyId,
  useIcdCodes,
} from "../../../../../stores/MasterTables";
import { DoctorContext } from "../../../../../stores/Doctor/doctor.provider";
import { RxStoreContext } from "../../../../../stores/Rx/rx.provider";
import { NoteDto, RxDto, RxDtoProcessingStatusEnum } from "../../../../../api-client";
import { v4 as uuidv4 } from "uuid";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { confirmDoctorClinicJoin } from "../../../../DoctorManagement/confirmDoctorClinicJoin";
import { confirmDoctorClinicMismatch } from "../../../../DoctorManagement/confirmDoctorClinicMismatch";
import { UserContext } from "../../../../../stores/User/User.provider";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import DoctorDialog from "../../../../PatientDataEntryForm/Doctor";
import { PatientsContext } from "../../../../../stores/Patients/patients.provider";
import ValidationCardRxInfoPage from "../../../../../molecules/ValidationCardRxInfoPage";
import FlatCard from "../../../../../atoms/FlatCard";
import classes from "./Blankoverordnung.module.css";
import ValidationCard, { formType } from "../../../../../molecules/ValidationCard";
import WarningDialog from "../../../../../atoms/WarningDialog";
import debounce from "lodash.debounce";
import ValidationTooltip from "../../../../../atoms/validationToolTip";
import DoctorCard from "../../../../../molecules/DoctorCard";
import { NotesContext } from "../../../../../stores/Notes/notes.provider";
import Notes from "../../../../ScheduleOverview/Notes/Notes";
import moment from "moment";
import { validateFrequency, validateTherapy } from "../../../../../molecules/Editable";
import { rxuseForm } from "../../../../PatientDataEntryForm/Form";
import {
  ClinicForModal,
  Doctor,
} from "../../../../DoctorManagement/DoctorTabContent/doctor-client-types";
import VerordnungDialog from "../Kasse/VerordnungDialog";
import SimpleDocumentViewer from "../../../../Documents/SimpleDocumentViewer";
import { useRx } from "../../../../../stores/Rx/rx.queries";
import { usePatient } from "../../../../../stores/Patients/patient.queries";
import { useSaveRxs } from "../../../../../stores/Rx/rx.mutations";
import { EntryType } from "../../../../PatientDataEntryForm/Receipt";
import { formTherapyFromLine } from "../../../../../helpers/therapyRxLabel";
import { RxFormRow, rowNamesTerm, rowTermText } from "../../../../../helpers/rxFormRow";
import TherapyTermField from "../../../../../molecules/TherapyTermField";
import { usePrescribedTherapyOptions } from "../../../../../stores/MasterTables/masterTables.queries";

interface DiagnoseGroup {
  a: string;
  b: string;
  c: string;
  d: string;
}

const styles = {
  mainHeader: {
    width: "100%",
    padding: "8px",
    backgroundColor: "#e0f7fa",
    borderRadius: "5px",
    fontWeight: "bold",
    marginBottom: "8px",
    textAlign: "center",
  },
  subHeader: {
    width: "100%",
    padding: "8px",
    backgroundColor: "#f1f8e9",
    borderRadius: "5px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "8px",
  },
  button: {
    width: "100%",
    backgroundColor: "#bbdefb",
    marginBottom: "8px",
    fontWeight: "bold",
  },
  arrow: {
    fontSize: "large",
    marginBottom: "8px",
  },
};

interface BlankoverordnungProps {
  selectedRxId?: string;
  patientId?: number;
  handleNewFormData?: (data: RxDto) => void;
  newFormData?: any;
  values?: any; //required only entryType is newRx
  readOnlyView?: boolean;
  entryType?: EntryType;
  openRx?: boolean;
  onCloseRx?: any;
  updateRxProcessingStatus?: (event: any) => void;
}

const ScalableDiv = styled("div")(({ theme }) => ({
  transformOrigin: "top left",
  [theme.breakpoints.down("sm")]: {
    transform: "scale(0.6)",
  },
  [theme.breakpoints.between("sm", "xl")]: {
    transform: "scale(0.65)",
  },
  [theme.breakpoints.up("xl")]: {
    transform: "scale(0.85)",
  },
}));

const ScalableComponentSimple = ({ children }: any) => {
  return <ScalableDiv>{children}</ScalableDiv>;
};

const Blankoverordnung: React.FC<BlankoverordnungProps> = observer(
  ({
    selectedRxId,
    patientId,
    handleNewFormData,
    values,
    newFormData,
    readOnlyView,
    entryType,
    onCloseRx,
    openRx,
    updateRxProcessingStatus,
  }) => {
    // const { rxFormData, patientValues, handleData, masterTablesData, readOnlyView } = props;
    const { data: masterTablesDataQuery } = useMasterTables();
    // Prescribed TERMS — the catalog level a prescription names (MD-232).
    const { data: termOptions = [] } = usePrescribedTherapyOptions();
    // Fetch RX data using the rxId from store
    const { data: rxData, isLoading: isRxLoading, error: rxError } = useRx(selectedRxId!);
    const rxFormData = entryType && newFormData ? newFormData : rxData;
    // A saved prescription being edited, as opposed to a new one. Several
    // rules below turn on this: a saved Rx keeps the group it was billed
    // against, a new one must not inherit anything.
    const editingSavedRxNumber = rxFormData?.rxNumber;

    // Get patient data from store
    const { data: patientData } = usePatient(patientId!);
    const patientValues = entryType === EntryType.newRx && values ? values : patientData;
    const { mutate: patientSaveRxs } = useSaveRxs();
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);
    const [currentICD, setCurrentICD] = useState(null)
    const { data: icdObject } = useIcdCodes(currentICD,false); // disabled initially
    const UserStore = useStore(UserContext);
    const accounts = useUserAccounts();
    const patientStore = useStore(PatientsContext);
    const rxStore = useStore(RxStoreContext);
    const { savedSeriesForPatient } = rxStore;
    const { getLiveValidation } = patientStore;
    const { user } = UserStore;
    // Use prop masterTablesData or fallback to query data
    const safeMasterTablesData = masterTablesDataQuery || [];
    const [selectedDiagnoseGroup, setSelectedDiagnoseGroup] = useState<DiagnoseGroup | null>(null);
    const RxStore = useStore(RxStoreContext);
    const { saveRxs } = RxStore;
    const [loading, setLoading] = useState(false);
    // MD-514: opens once the tarif group is known, never before. It used to
    // default to true and so opened on mount — the one moment tarifGroupId is
    // guaranteed empty, which is exactly when its lookup cannot match.
    const [autoFill, setAutoFill] = useState(false);
    const prefillHasAutoOpened = useRef(false);
    // const [isDoctorNumberDisabled, setIsDoctorNumberDisabled] = useState(true);
    const [doesClinicExist, setDoesClinicExist] = useState(false);
    const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
    const [isClinicModalEditMode, setIsClinicModalEditMode] = useState(false);
    const [currentClinicId, setCurrentClinicId] = useState("");
    const [creationResponse, setCreationResponse] = useState<any>({
      id: "",
      clinics: [],
      contactInfo: {},
      doctorId: "",
      clinicId: "",
    });
    const [currentDoctorId, setCurrentDoctorId] = useState("");
    const [doesDoctorExist, setDoesDoctorExist] = useState(false);
    const [currentClinic, setCurrentClinic] = useState<ClinicForModal | undefined>();
    const [showClinicData, setShowClinicData] = useState(false);
    const [currentDoctor, setCurrentDoctor] = useState<Doctor | ClinicForModal>();
    const [autocompleteSearchTerm, setAutocompleteSearchTerm] = useState("");

    // Position und DocumentViewer State-Variablen
    const [position, setPosition] = useState<"default" | "right-third">("default");
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

    useEffect(() => {
      setCurrentDoctor(rxFormData?.doctor as any);
      setCurrentClinic(rxFormData?.clinic as any);
    }, [rxFormData?.doctor, rxFormData?.clinic]);

    // Keep display fields in sync with the actual doctor/clinic LANR/BSNR
    useEffect(() => {
      if (currentDoctor) {
        const lanr = (currentDoctor as any).LANR || "";
        const docId = (currentDoctor as any).id || "";
        setFormData((prev) => ({ ...prev, doctorNumber: lanr, doctorId: docId }));
      }
    }, [currentDoctor]);

    useEffect(() => {
      if (currentClinic) {
        const bsnr = (currentClinic as any).BSNR || "";
        const clinId = currentClinic.id || "";
        setFormData((prev) => ({ ...prev, practiceNumber: bsnr, clinicId: clinId }));
      }
    }, [currentClinic]);

    useEffect(() => {
      if (creationResponse && creationResponse.doctorId && creationResponse.clinicId) {
        setCurrentDoctor({
          id: creationResponse.doctorId,
          LANR: creationResponse.LANR,
          contactInfo: creationResponse.contactInfo as any,
          clinics: creationResponse.clinics,
        });
        const matchedClinic = (creationResponse.clinics || []).find((clinic: any) => clinic.id === creationResponse.clinicId);
        setCurrentClinic(matchedClinic as any || undefined);
        setFormData((prev) => ({
          ...prev,
          doctorId: creationResponse.doctorId || "",
          clinicId: creationResponse.clinicId || "",
          doctorNumber: creationResponse.LANR || "",
          practiceNumber: matchedClinic?.BSNR || "",
        }));
      }
    }, [creationResponse]);

    // console.log("sakjakjskjdakj", rxFormData, patientValues);

    //For payer
    const insurer = safeMasterTablesData
      .filter((obj: any) => "insurer" in obj)
      .map((obj: any) => obj.insurer);
    const [selectedPayer, setSelectedPayer] = useState(null);
    const [selectedChipCard, setSelectedChipCard] = useState<string | null>(null);
    const diagnoseGroups = safeMasterTablesData
      .filter((obj: any) => "diagnosisGroups" in obj)
      .map((obj: any) => obj.diagnosisGroups);
    const rxType = safeMasterTablesData
      .filter((obj: any) => "rxType" in obj) // Filter objects that have the insuranceStatus property
      .map((obj: any) => obj.rxType);
    //  therapyOptions= therapyOptions[0].filter(
    //       (therapyObj: any) =>

    //         therapyObj.duration
    //     )

    //For Doctor
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const DoctorStore = useStore(DoctorContext);
    const { getClinicById, getDoctorById, createDoctorWithClinic } = DoctorStore;
    const [isSelectActive, setIsSelectActive] = useState(false);
    const [isAddEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const NotesStore = useStore(NotesContext);
    const { getRxNotes, updateNoteById, postNotes, fetchNotes } = NotesStore;
    const [rxNotes, setRxNotes] = useState<NoteDto[]>([]);
    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await getRxNotes(selectedRxId!);

          setRxNotes(response);
        } catch (error) {
          // console.log("error", error);
        }
      };

      fetchData();
    }, [selectedRxId, fetchNotes]);
    const updateNote = async (index: number, updatedCard: NoteDto) => {
      updatedCard.rxId = rxFormData?.id;
      if (updatedCard.id) {
        await updateNoteById(updatedCard.id, user?.id as string, updatedCard);
      } else {
        await postNotes(user?.id as string, updatedCard);
      }
      const updatedNotes = [...rxNotes];
      updatedNotes[index] = updatedCard;
      setRxNotes(updatedNotes);
    };

    const account = safeMasterTablesData
      .filter((obj: any) => "account" in obj)
      .map((obj: any) => obj.account);

    const handlePersonSearchClick = () => {
      setIsSelectActive(!isSelectActive); // Activate the Select dropdown
    };

    const handleButtonClick = (therapyType: string) => {
      setUnsavedChanges(true);
      // const updatedTherapyRxs = [...formData.therapyRxs];pe
      const updatedTherapyRxs: any[] = [];
      let isHomeVisit: any = null; // Initialize

      let therapiesToAdd: any = [
        { abbreviation: "PD", amount: 1 },
        { abbreviation: "BD", amount: 1 },
      ];
      switch (therapyType) {
        case "Kein Bruch":
          therapiesToAdd.push(
            { abbreviation: "MT", amount: 5 },
            { abbreviation: "KG", amount: 5 },
            { abbreviation: "KGG", amount: 8 }
          );
          isHomeVisit = false;
          break;
        case "Bruch":
          therapiesToAdd.push(
            { abbreviation: "MT", amount: 8 },
            { abbreviation: "KG", amount: 8 },
            { abbreviation: "KGG", amount: 10 }
          );
          isHomeVisit = false;
          break;
        case "Therapeut hat MT":
          therapiesToAdd.push(
            { abbreviation: "MT", amount: 10 },
            { abbreviation: "KG", amount: 8 }
          );
          isHomeVisit = true;
          break;
        case "Kein MT":
          therapiesToAdd.push({ abbreviation: "KG", amount: 18 });
          isHomeVisit = true;
          break;
        default:
          break;
      }
      // therapiesToAdd.forEach((therapyObj: any, index: number) => {
      //   const selectedTherapy = therapyOptions?.find(
      //     (option: any) => option.abbreviation === therapyObj.abbreviation
      //   );
      //   if (selectedTherapy) {
      //     updatedTherapyRxs[index] = {
      //       ...updatedTherapyRxs[index],
      //       id:updatedTherapyRxs[index]?.id || uuidv4(),
      //       therapy: {
      //         id: selectedTherapy.id,
      //         abbreviation: selectedTherapy?.abbreviation,
      //         description: selectedTherapy?.description || "",
      //         price: selectedTherapy?.price || "",
      //         HMP4: ""
      //       },
      //       amount: therapyObj.amount,
      //       frequency: selectedTherapy.frequency || formData.therapyRxs[0]?.frequency || "1-5"
      //     };
      //   }

      // MD-514: without a tarif group NOTHING below can match, and the padding
      // further down would then replace whatever the user already has with
      // three empty rows — silently. Say so and change nothing instead.
      if (!formData.tarifGroupId) {
        notificationStore.showMessage(
          "Bitte zuerst die Tarifgruppe wählen — die Ausfüllhilfe sucht die Heilmittel darin.",
          "error"
        );
        return;
      }

      // Suggestions this practice's catalog has no line item for, under the
      // chosen tarif group. Collected rather than skipped in silence: a tile
      // that quietly delivers two of its three Heilmittel is worse than one
      // that says which it could not find.
      const unavailable: string[] = [];

      // Assign therapies from `therapyOptions`
      therapiesToAdd.forEach((therapyObj: any, index: number) => {
        const selectedTherapy = therapyOptions?.find(
          (option: any) =>
            option.abbreviation === therapyObj.abbreviation &&
            option.tarifGroup.id === formData.tarifGroupId
        );
        if (!selectedTherapy) unavailable.push(therapyObj.abbreviation);
        if (selectedTherapy) {
          updatedTherapyRxs[index] = {
            ...updatedTherapyRxs[index],
            id: updatedTherapyRxs[index]?.id || uuidv4(),
            // MD-232: the line must NAME the prescribed term as text. The
            // server resolves rawText -> alias -> term on save and rejects a
            // line that names none with 'Heilmittel "unbekannt"'. The `therapy`
            // object below is display-only and is dropped by
            // toTherapyRxWriteDto, so a prefilled Blanko Rx previously reached
            // the server carrying no term at all and never saved. Every
            // abbreviation this builder uses (PD, BD, MT, KG, KGG) is both a
            // catalog alias and a term id, so it resolves as-is.
            rawText: therapyObj.abbreviation,
            therapy: { ...selectedTherapy, isSupplementary: false },
            amount: therapyObj.amount,
            frequency: selectedTherapy.frequency || formData.therapyRxs[0]?.frequency || "1-5",
          };
        }
      });

      // Ensure 3 main therapy rows
      while (updatedTherapyRxs.length < 3) {
        updatedTherapyRxs.push({
          id: uuidv4(),
          therapy: { id: "", abbreviation: "", isSupplementary: false },
          amount: "",
          frequency: { id: "1-5", text: "1-5" },
        });
      }

      // Ensure 1 supplementary therapy row
      updatedTherapyRxs.push({
        id: uuidv4(),
        therapy: { id: "", abbreviation: "", isSupplementary: true },
        amount: "",
        frequency: { id: "1-5", text: "1-5" },
      });

      // Update form data
      setFormData((prevFormData) => ({
        ...prevFormData,
        therapyRxs: updatedTherapyRxs,
        isHomeVisit: isHomeVisit, // Set the  at the form level
      }));

      setHausbesuchSelected(isHomeVisit);

      if (unavailable.length) {
        notificationStore.showMessage(
          `Nicht im Katalog dieser Tarifgruppe: ${unavailable.join(", ")}. ` +
            "Die übrigen Heilmittel wurden übernommen.",
          "warning"
        );
      }

      handleClose(); // Optionally close the dialog
    };

    /**
     * "" is what an untouched form field holds; null is what a nullable column
     * accepts. Only the empty string is converted — false, 0 and a real value
     * all pass through, so an explicitly unticked box stays unticked.
     */
    const emptyToNull = <T,>(value: T): T | null =>
      (value as unknown) === "" ? null : value;

    const initialrxValues = {
      Receiptpayer: patientValues?.payer,
      address: "",

      status: "",
      signatureStatus: "",
      insuranceStatus: "",
      // Kostenträgererkennung. Seeded from the patient's payer, the same value
      // the payer dropdown writes here when it changes (`selectedPayer?.ik`)
      // and the same one the Kasse route gets from `selectedInsurer?.ik` on
      // the way in. Left as "" this was the only insurance field on the form
      // with no fallback to the patient record — `insureeNumber` and
      // `insuranceStatus` reach theirs through resolveSourcedValue — so a new
      // Blankoverordnung opened with the Kostenträgererkennung blank.
      chipcard: (patientValues as any)?.payer?.ik ?? "",
      // NOT hardcoded to "Blanko": a Blankoverordnung is billed against
      // Blanko, PostBlanko or — once seeded — ErgoBlanko. The effect below
      // picks it when the catalog leaves only one, otherwise the user does.
      tarifGroupId: "",
      insureeNumber: "",
      rxtype: "",
      accountId: "",

      isPerpetual: false,

      practiceNumber: "",
      doctorNumber: "",
      doctorId: "",
      clinicId: "",
      date: "",
      isCopaymentExempted: false,
      isAccident: false,
      isBVG: false,
      icdCodeEntries: [{ userText: "" }, { userText: "" }],
      diagnosisText: "",
      diagnosegroup: null,
      a: "",
      b: "",
      c: "",
      d: "",
      hasIndividualSymptoms: false,
      therapyRxs: [
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "1-5", text: "1-5", prefferedValue: "" },
          therapy: {
            id: "",
            HMP4: "",
            price: "",
            abbreviation: "",
            description: "",
            isSupplementary: false,
          },
          // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
      ],
      isHomeVisit: rxFormData?.isHomeVisit ?? null,
      therapyReport: false,
      urgent: false,
      symptoms: "",
      therapyFrequency: "",
      amount: "",
      additionalNotes: "",
      duplicate: false,
      processingStatus: RxDtoProcessingStatusEnum.InBehandlung,
      clinic: {} as any,
    };

    const [formData, setFormData] = useState(initialrxValues);

    // console.log("zauuausjsjjs", formData);

    // Patient-only fields: never live on the Rx entity, so read them straight
    // from the patient record (the single source of truth).
    const patientLastName = patientValues?.lastName || "";
    const patientFirstName = patientValues?.firstName || "";
    const patientDob = patientValues?.dob ? moment(patientValues.dob).format("YYYY-MM-DD") : "";

    // Dual-sourced fields (saved on both the Rx and the patient record). formData
    // holds the Rx-native value; the resolver applies the patient fallback for
    // display while exposing `.source` ("rx" | "patient" | "none") so the UI can
    // make the data's origin transparent to the user.
    const insureeNumberField = resolveSourcedValue<string>(
      formData?.insureeNumber,
      patientValues?.insuranceNo,
    );
    const insuranceStatusField = resolveSourcedValue<string>(
      formData?.insuranceStatus,
      patientValues?.status,
    );

    const [icdCodeEntries, setIcdCodeEntries] = useState(initialrxValues.icdCodeEntries);
    const [mainTherapies, setMainTherapies] = useState([]);
    const [supplementaryTherapies, setSupplementaryTherapies] = useState([]);

    const therapyOptions = safeMasterTablesData
      .filter((obj: any) => "therapy" in obj)
      .map((obj: any) => obj.therapy)[0];

    // Derive therapyType from the selected account (Physio vs. Ergo)
    //
    // `accounts` are the Mandanten of the location the user is logged into, so a
    // Rx belonging to another location's account (opened from Langenhagen, say,
    // a "Braunschweig Ergo" Rx) found nothing there and fell back to Physio —
    // ticking Physiotherapie and offering the Physio Diagnosegruppe EX on an
    // Ergo Blankoverordnung. The Rx knows its own account and discipline.
    const accountTherapyType = useMemo(() => {
      const selectedAccount = accounts?.find(
        (a: any) => a.name === formData.accountId || a.id === formData.accountId
      );
      if (selectedAccount?.therapyType) return selectedAccount.therapyType;
      const rxAccount = (rxFormData as any)?.account;
      const rxAccountStillSelected =
        !formData.accountId ||
        rxAccount?.name === formData.accountId ||
        rxAccount?.id === formData.accountId;
      if (rxAccountStillSelected && rxAccount?.therapyType) return rxAccount.therapyType;
      return (rxAccountStillSelected && (rxFormData as any)?.therapyType) || "Physiotherapie";
    }, [formData.accountId, accounts, rxFormData]);

    // Only the groups the chosen Rezept-typ can be billed against — narrowed
    // further to the ones this practice's therapy type actually has line items
    // for, so the invariant cannot be broken by hand either.
    // The group currently on the form, but only if the Mandant's discipline
    // actually serves it.
    //
    // `narrowToTherapyType` keeps `currentTarifGroupId` in the options even
    // when the therapy type does not serve it — deliberately, so editing a
    // SAVED prescription cannot silently rewrite the group it was billed
    // against. On a new prescription that protection works against us twice
    // over: picking the Ergo Mandant and switching back to Physio left
    // ErgoBlanko both offered in the dropdown and immune to being cleared,
    // because `keptCurrent` then saw it among the options.
    //
    // So for a new prescription, a group the Mandant cannot serve is treated
    // as no group at all. A group the Mandant CAN serve is still preserved —
    // this must not undo a deliberate choice of Blanko vs PostBlanko.
    const effectiveTarifGroupId = useMemo(() => {
      const current = formData?.tarifGroupId ?? "";
      if (!current || editingSavedRxNumber) return current;
      const served = getTarifGroupsForTherapyType(safeMasterTablesData, accountTherapyType);
      if (!served) return current;
      return served.includes(current) ? current : "";
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.tarifGroupId, editingSavedRxNumber, safeMasterTablesData, accountTherapyType]);

    const tarifGroupResolution = useMemo(
      () =>
        resolveTarifGroupForRxType(
          safeMasterTablesData,
          formData?.rxtype,
          effectiveTarifGroupId,
          accountTherapyType
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [safeMasterTablesData, formData?.rxtype, effectiveTarifGroupId, accountTherapyType]
    );
    const tarifGroupOptions = tarifGroupResolution.options;

    // The tarif group follows whatever narrows it — the Rezept-typ AND the
    // Mandant, whose discipline decides which groups have line items at all.
    //
    // MD-514: this used to only ever SET, and only when the catalog left
    // exactly one candidate. It never cleared one that had stopped being
    // valid. Picking the Ergo Mandant set ErgoBlanko (its only option), and
    // switching back to the Physio Mandant left ErgoBlanko standing, because
    // Physio has two candidates so the "exactly one" rule declined to act.
    // The form then held a tarif group from the wrong discipline, and every
    // Heilmittel lookup against it — the Ausfüllhilfe's included — matched
    // nothing.
    //
    // `nextTarifGroupId` is what the resolver already computes for this: keep
    // the current group when it survives the narrowing, take the only
    // candidate when there is one, otherwise clear it and let the user choose.
    // The Rezept-typ handler has always used it; this path never did.
    useEffect(() => {
      const next = tarifGroupResolution.nextTarifGroupId;
      const current = formData?.tarifGroupId ?? "";
      if (next === current) return;
      setFormData((prevValues: any) => ({ ...prevValues, tarifGroupId: next }));
      if (!next && current) {
        notificationStore.showMessage(
          "Bitte wählen Sie eine Tarifgruppe für den gewählten Mandanten.",
          "warning"
        );
      }
    }, [tarifGroupResolution, formData?.tarifGroupId]);

    // MD-514: the Ausfüllhilfe matches its suggestions against the chosen tarif
    // group, so it is worthless until one is set. For a Physio practice the
    // effect above never fires (Blanko vs PostBlanko is the user's call), so
    // the dialog's old "open on mount" could only ever produce empty rows.
    // Open it the moment the group is known instead, once per form.
    useEffect(() => {
      if (prefillHasAutoOpened.current) return;
      if (readOnlyView || selectedRxId) return;
      if (accountTherapyType === "Ergotherapie") return;
      if (!formData?.tarifGroupId) return;
      prefillHasAutoOpened.current = true;
      setAutoFill(true);
    }, [formData?.tarifGroupId, readOnlyView, selectedRxId, accountTherapyType]);

    // The Diagnosegruppen this Rx can use: its Rezept-typ, narrowed to the
    // practice's discipline (Blankoverordnung → EX for Physio; SB1, PS3, PS4 for
    // Ergo). Every lookup of the chosen group goes through this list first.
    const filteredDiagnoseGroups: any[] = useMemo(
      () =>
        diagnosisGroupsForRx(
          diagnoseGroups[0],
          // formData.rxtype holds the type's text after loading an Rx and its id
          // after a change in the select; the catalog is keyed by id.
          rxType[0]?.find((option: any) => option.text === formData?.rxtype)?.id ??
            formData?.rxtype,
          accountTherapyType
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [safeMasterTablesData, formData?.rxtype, accountTherapyType]
    );

    // Pre-fill the group only when there is nothing to choose — the same rule
    // as the tarif group above. Also clears a group that does not belong to this
    // Rx (e.g. an "EX" left over from before the Rezept-typ or practice was set).
    useEffect(() => {
      if (readOnlyView || filteredDiagnoseGroups.length === 0) return;
      const current = formData?.diagnosegroup;
      const belongs =
        !!current &&
        filteredDiagnoseGroups.some((g: any) => g.code === current || g.id === current);
      if (belongs) return;
      const fallback = defaultDiagnosisGroup(filteredDiagnoseGroups);
      const next = fallback ? fallback.code : "";
      if ((current ?? "") === next) return;
      setFormData((prevValues: any) => ({ ...prevValues, diagnosegroup: next }));
    }, [filteredDiagnoseGroups, formData?.diagnosegroup, readOnlyView]);

    const filterTherapies = () => {
      if (!therapyOptions) return;

      const main = therapyOptions.filter(
        (option: any) => option.tarifGroup.id === formData.tarifGroupId && !option.isSupplementary &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );

      const supplementary = therapyOptions.filter(
        (option: any) => option.tarifGroup.id === formData.tarifGroupId && option.isSupplementary &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );

      setMainTherapies(main);
      setSupplementaryTherapies(supplementary);
    };

    useEffect(() => {
      filterTherapies();
    }, [formData.rxtype, formData.tarifGroupId, accountTherapyType]);
    const handleUnsavedChangesWarning = () => {
      handleGenericSubmit();
    };
    // const updateRxProcessingStatus = async (event: any) => {
    //   const response = await updateprocessingStatus(rxFormData.id, event?.target.value);
    // };
    const handleCloseRxDialog = () => {
      // console.log("shjsakjsajj", unsavedChanges);
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onCloseRx();
      }
    };
    const handleCloseUnsavedChangesWarning = () => {
      onCloseRx();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    // useEffect(() => {
    //   if (rxFormData) {
    //     // Populate the form data with values from rxFormData
    //     setFormData(rxFormData);
    //   } else {
    //     // Use empty values for form data
    //    setFormData(initialrxValues)
    //   }
    // }, [rxFormData]);
    // Initialize with two input boxes

    // Expose the submitForm function to the parent component

    const formattedRxData = (rxFormData: any, initialTherapyRxs: any) => {
      let icdCodeEntries;

      if (rxFormData.icdCodeEntries && rxFormData.icdCodeEntries.length > 0) {
        if (rxFormData.icdCodeEntries.length === 1) {
          icdCodeEntries = [...rxFormData.icdCodeEntries, { userText: "" }];
        } else {
          icdCodeEntries = rxFormData.icdCodeEntries;
        }
      } else {
        icdCodeEntries = [{ userText: "" }, { userText: "" }];
      }
      const updatedFormData = {
        ...rxFormData,
        diagnosisText: rxFormData?.diagnosisText || "",
        additionalNotes: rxFormData?.additionalNotes || "",
        symptoms: rxFormData?.symptoms || "",

        // Keep these Rx-native. The patient-record fallback is applied at the
        // display/save boundary via resolveSourcedValue so provenance (Rx vs
        // patient) stays distinguishable. Patient-only fields (name, DOB) are
        // not snapshotted here and are read directly from patientValues.
        insureeNumber: rxFormData?.insureeNumber || "",
        insuranceStatus: rxFormData?.insuranceStatus || "",
        // Receiptpayer: rxFormData.payer.insurer || "",
        Receiptpayer:
          insurer[0]?.find((option: any) => option.id === rxFormData.Receiptpayer)?.name ||
          rxFormData.payer?.name ||
          rxFormData.Receiptpayer,
        chipcard: rxFormData.chipcard || rxFormData.payer?.ik,
        // No fallback group here: "EX" was hard-coded, which is the Physio
        // Blanko group and does not exist for Ergotherapie. A group is pre-filled
        // only when the Rx leaves exactly one to choose (see the effect below).
        diagnosegroup:
          diagnoseGroups[0]?.find((option: any) => option.id === rxFormData.diagnosegroup)?.code ||
          rxFormData.diagnosisGroup?.code ||
          "",
        rxtype:
          rxType[0]?.find((option: any) => option.id === rxFormData.rxtype)?.text ||
          rxFormData.rxtype?.text ||
          "",
        // The Rx's own tarif group. Hardcoding "Blanko" here rewrote every
        // PostBlanko Rx this form opened — and would have swallowed
        // ErgoBlanko the same way.
        tarifGroupId: rxFormData.tarifGroupId || rxFormData.tarifGroup?.id || "",
        accountId:
          account[0]?.find((option: any) => option.id === rxFormData.accountId)?.name ||
          rxFormData.account?.name,
        status: rxFormData.status || "",
        signatureStatus: rxFormData.signatureStatus || "",
        therapyRxs: initialTherapyRxs,
        // rxtype:rxFormData.rxtype?.id || "",
        // accountId:rxFormData.account?.id || "",
        // chipcard: rxFormData.payer?.id || "",
        // doctorId: rxFormData.doctor?.contactInfo?.lastName || "",
        bsnr: rxFormData.clinicId || rxFormData.practiceNumber || "",
        // diagnosegroup: rxFormData.diagnosisGroup?.code || "",
        icdCodeEntries: icdCodeEntries,
        // Map new API field names to form's internal keys
        doctorNumber: rxFormData.LANR || "",
        practiceNumber: rxFormData.BSNR || "",
        doctorId: rxFormData.doctorId || rxFormData.doctorNumber || "",
        clinicId: rxFormData.clinicId || rxFormData.practiceNumber || "",
      };
      return updatedFormData;
    };

    useEffect(() => {
      const fetchData = async () => {
        if (rxFormData && rxFormData.rxNumber) {
          let rxs: [] = [];

          rxFormData.therapyRxs = rxFormData?.therapyRxs?.flatMap((therapy: any): RxFormRow => {
            // Extract therapyRxs from each rx, or default to an empty array
            // Map therapyRxs to the desired format
            // console.log("Therapy Log DATA", therapy, therapy.frequency);
            return {
              id: therapy.id,
              date: rxFormData.date,
              // Carried through the round trip: this is what names the term on
              // save. An API line has `rawText`; fall back to the term id so a
              // line stored before rawText existed still resolves.
              rawText: therapy.rawText ?? therapy.prescribedTherapyId ?? null,
              therapy: formTherapyFromLine(therapy, therapyOptions),
              amount: therapy.amount,
              frequency: therapy.frequency
                ? therapy.frequency.id
                  ? therapy.frequency
                  : { id: therapy?.frequency, text: therapy?.frequency }
                : null,
              // Doubling is `unitsPerDay` since MD-232 — the server no longer
              // sends a persisted `isDoubled`. Carry both: the checkbox reads
              // the boolean, every save channel reads `unitsPerDay`.
              unitsPerDay: therapy.unitsPerDay,
              isDoubled: (therapy.unitsPerDay ?? (therapy.isDoubled ? 2 : 1)) > 1,
              rxSeries: rxFormData.rxSeries ?? therapy.rx?.rxSeries,
            };
          });

          // Check if supplementary therapy exists
          const hasSupplementary = rxFormData?.therapyRxs?.some(
            (therapy: any) => therapy.therapy?.isSupplementary
          );

          let finalTherapyRxs: any = [...(rxFormData?.therapyRxs ?? [])];

          // If no supplementary therapy exists, ensure we add one
          if (!hasSupplementary) {
            finalTherapyRxs = [
              ...(rxFormData?.therapyRxs ?? []),
              {
                id: uuidv4(),
                amount: "",
                frequency: { id: "1-5", text: "1-5" },
                therapy: {
                  id: "",
                  HMP4: "",
                  price: "",
                  abbreviation: "",
                  description: "",
                  isSupplementary: true, // Add supplementary therapy
                },
                // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
                rxSeries: { label: "" },
              },
            ];
          }

          // Ensure we always have 4 therapy rows, keeping existing data intact
          while (finalTherapyRxs.length < 4) {
            finalTherapyRxs.push({
              id: uuidv4(),
              amount: "",
              frequency: { id: "", text: "", preferredValue: "" },
              therapy: {
                id: "",
                HMP4: "",
                price: "",
                abbreviation: "",
                description: "",
                isSupplementary: false, // Default to main therapy
              },
              // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
              rxSeries: { label: "" },
            });
          }

          // Update form data based on fetched data

          const updatedFormData = formattedRxData(rxFormData, finalTherapyRxs);
          // Update form data state
          setFormData(updatedFormData);

          // Optionally, return the updated form data
          return updatedFormData;
        }
        // Make API request to fetch patient data based on patient ID or any other identifier
        else {
          const hasSupplementary = rxFormData?.therapyRxs?.some(
            (therapy: any) => therapy.therapy?.isSupplementary
          );

          const therapyRxs =
            rxFormData?.therapyRxs && rxFormData?.therapyRxs.length
              ? Array.from({ length: 4 }, (_, index) => ({
                  ...rxFormData?.therapyRxs?.[index],
                  id: rxFormData?.therapyRxs?.[index]?.id || uuidv4(),
                  amount: rxFormData?.therapyRxs?.[index]?.amount || "",
                  frequency: (rxFormData?.therapyRxs?.[index]?.frequency as any)?.id
                    ? rxFormData?.therapyRxs?.[index]?.frequency
                    : { id: "1-5", text: "1-5" },
                  therapy: rxFormData?.therapyRxs?.[index]?.therapy
                    ? rxFormData?.therapyRxs?.[index]?.therapy
                    : {
                        id: "",
                        abbreviation: "",
                        isSupplementary: hasSupplementary ? false : index === 3, // Ensure at least one supplementary
                      },
                }))
              : initialrxValues.therapyRxs;

          const updatedData = formattedRxData(rxFormData, therapyRxs);
          setFormData(updatedData);
        }
      };
      fetchData();

      // Only Rx-native data is snapshotted into formData now, so this effect just
      // tracks rxFormData. Patient-record fields (name, DOB, and the insurance
      // fallbacks) are read directly from patientValues at render time, which also
      // removes the prior timing bug where the patient loading after the Rx left
      // the name blank when navigating directly to an Rx.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rxFormData]);

    /**
     * MD-514: the effect above seeds the form only from a SAVED prescription
     * (it guards on `rxNumber`). Anything else reaching `rxFormData` is not a
     * prescription to edit — it is leftover state from the last one, because
     * `newFormData` is handed back from a value the parent never reset.
     *
     * A form with no saved Rx behind it is a NEW prescription and must start
     * clean. Without this the previous prescription's Mandant and tarif group
     * reappeared, and `accountTherapyType` — which falls back through
     * `rxFormData.account` and `rxFormData.therapyType` when no Mandant is
     * chosen — then inferred Ergotherapie on a Physio patient, dragging the
     * tarif group to ErgoBlanko with it.
     *
     * Keyed on the identity of what is being edited, never on `formData`, so
     * it cannot fight the user's typing.
     */
    useEffect(() => {
      if (editingSavedRxNumber) return;
      // Keep the Rezept-typ. It is the one field the user has already decided
      // by the time this form exists — it is what selected this form — and
      // `initialrxValues` carries it as "". Blanking it broke the very
      // narrowing this reset was meant to enable: with no Rezept-typ,
      // resolveTarifGroupForRxType finds no groups for it and falls back to
      // offering EVERY tarif group, so the dropdown stopped filtering at all.
      setFormData((prev: any) => ({ ...initialrxValues, rxtype: prev?.rxtype ?? "" }));
      prefillHasAutoOpened.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingSavedRxNumber, selectedRxId, patientId]);
    useEffect(() => {
      if (formData.diagnosegroup) {
        // Scoped to this Rx's groups: "PS4" is both a Kasse and a Blanko group.
        const selectedDiagnoseGroup: any = findDiagnosisGroup(
          formData.diagnosegroup,
          filteredDiagnoseGroups,
          diagnoseGroups[0]
        );

        if (selectedDiagnoseGroup) {
          // Generate symptoms based on selected checkboxes
          let selectedSymptoms = "";
          if (formData.a) selectedSymptoms += selectedDiagnoseGroup.a + " ";
          if (formData.b) selectedSymptoms += selectedDiagnoseGroup.b + " ";
          if (formData.c) selectedSymptoms += selectedDiagnoseGroup.c + " ";
          if (formData.d) selectedSymptoms += selectedDiagnoseGroup.d + " ";
          setSelectedDiagnoseGroup(selectedDiagnoseGroup);
          setFormData((prevValues: any) => ({
            ...prevValues,
            a: selectedDiagnoseGroup.a ? prevValues.a : false,
            b: selectedDiagnoseGroup.b ? prevValues.b : false,
            c: selectedDiagnoseGroup.c ? prevValues.c : false,
            d: selectedDiagnoseGroup.d ? prevValues.d : false,
            ...(prevValues.hasIndividualSymptoms ? {} : { symptoms: selectedSymptoms.trim() }),
          }));
        }
      }
    }, [formData.diagnosegroup, formData.a, formData.b, formData.c, formData.d, filteredDiagnoseGroups]);

    //Could not find better way to implement this icdCode
        const handleICDCodeEntrySubmit = useCallback(
          async (icdEntry: any, index: number) => {
    
            try {
              setFormData((prevValues: any) => {
                const updatedICDCodeEntries = prevValues.icdCodeEntries.map((entry: any, i: number) =>
                  i === index ? { id: icdEntry.id || "", userText: icdEntry.userText } : entry
                );
    
                // Avoid duplicating diagnosis text
                const existingICDTexts = prevValues.icdCodeEntries.map((e: any) => e.userText);
                const newDiagnosisText = existingICDTexts.includes(icdObject.text)
                  ? prevValues.diagnosisText
                  : prevValues.diagnosisText
                  ? `${prevValues.diagnosisText} ${icdObject.text}`
                  : icdObject.text;
    
                return {
                  ...prevValues,
                  icdCodeEntries: updatedICDCodeEntries,
                  diagnosisText: newDiagnosisText.trim(),
                };
              });
            } catch (error) {
              console.error("Error updating ICD entry:", error);
            }
          },
          [icdObject]
        );
    
        useEffect(() => {
          if (icdObject) {
            // Use latest index and currentICD
            const index = icdCodeEntries.findIndex((e) => e.userText === currentICD);
            handleICDCodeEntrySubmit(icdCodeEntries[index], index);
          }
        }, [icdObject]);

    // const handleICDCodeEntrySubmit = async (icdCode: any, index: number) => {
    //   try {
    //     // TODO: Replace getICD call with React Query implementation
    //     const icdObject: any = null; // await getICD(icdCode.userText);
    //     if (icdObject) {
    //       // If the API call is successful and returns an icdObject, update the icdCodeEntries state with the normCode
    //       setFormData((prevValues: any) => {
    //         const updatedICDCodeEntries = prevValues.icdCodeEntries.map(
    //           (icdEntry: any, i: number) => {
    //             return i === index
    //               ? { id: icdCode?.id || "", userText: icdCode.userText }
    //               : icdEntry;
    //           }
    //         );

    //         const concatenatedDiagnosisText = prevValues.diagnosisText
    //           ? `${prevValues.diagnosisText} ${icdObject.text}` // Append the new icdObject.text to the existing diagnosisText
    //           : icdObject.text;
    //         return {
    //           ...prevValues,
    //           icdCodeEntries: updatedICDCodeEntries,
    //           diagnosisText: concatenatedDiagnosisText.trim(), // Set diagnosisText to the concatenated value
    //         };
    //       });
    //     }
    //   } catch (error) {
    //     console.error("Error fetching ICD object:", error);
    //   }
    // };

    const handleChange = (event: any, index?: any) => {
      const { name, value } = event.target;
      const updatedTherapyRxs: any = [...formData.therapyRxs];
      updatedTherapyRxs[index][name.split(".")[1]] = value;
      setFormData({ ...formData, therapyRxs: updatedTherapyRxs });
    };
    const handleOpenAddEditDialog = (lanr: string | null, bsnr: string | null) => {
      setAddEditDialogOpen(true);
    };

    const handleCloseAddEditDialog = (data: any) => {
      // Handle doctor data, LANR, and BSNR passed back from DoctorDialog
      setAddEditDialogOpen(false);

      // Here you can perform further actions with the received data
      if (data && "number" in data) {
        const { number, clinicNumber, contactInfo, id } = data;

        setSelectedDoctor(contactInfo?.lastName);
        setFormData((prevFormValues: any) => ({
          ...prevFormValues,
          doctorId: id || number,
          clinicId: data.clinicId || clinicNumber,
          doctorNumber: number,
          practiceNumber: clinicNumber,
        }));

        // For example, you can update doctor form values if needed
        // setDoctorFormValues(doctorData);
      }
    };
    const handleAddInput = () => {
      setIcdCodeEntries([...icdCodeEntries, { userText: "" }]);

      // Update the formValues state as well to reflect the new entry
      setFormData((prevValues: any) => ({
        ...prevValues,
        icdCodeEntries: [...prevValues.icdCodeEntries, { userText: "" }],
      }));
    };
    const [isHausbesuchSelected, setHausbesuchSelected] = useState<boolean | null>(
      formData.isHomeVisit
    );
    const handleCheckboxChange = (event: any) => {
      const selectedValue = event.target.value; // "yes" or "no"

      if (selectedValue === "yes") {
        setHausbesuchSelected(true);
        // Set isHomeVisit to true if "yes" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: true,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else if (selectedValue === "no") {
        setHausbesuchSelected(false);
        // Set isHomeVisit to false if "no" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: false,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setHausbesuchSelected(null); // Set null if neither is selected
        // Set isHomeVisit to null if neither checkbox is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: null,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleClose = () => setAutoFill(false);

    // const handleDeleteTherapy = (therapyId: string) => {
    //   // Filter out the therapy that matches the given ID
    //   const updatedTherapies = formData.therapyRxs.filter((therapy) => therapy.id !== therapyId);

    //   // Update formData state with new therapy list
    //   setFormData({
    //     ...formData,
    //     therapyRxs: updatedTherapies,
    //   });
    // };
    const handleDeleteTherapy = (therapyId: string) => {
      // Map over therapyRxs and clear the matching row's content
      const updatedTherapies = formData.therapyRxs.map((therapy) =>
        therapy.id === therapyId
          ? {
              ...therapy,
              amount: "",
              //  frequency: { id: "", text: "", prefferedValue: "" },
              therapy: {
                id: "",
                HMP4: "",
                price: "",
                abbreviation: "",
                description: "",
                isSupplementary: therapy.therapy.isSupplementary,
              },
              // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
              rxSeries: { label: "" },
            }
          : therapy
      );

      setFormData({
        ...formData,
        therapyRxs: updatedTherapies,
      });
    };

    const handlePracticeNumberInteraction = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("BSNR muss 9-stellig sein", "error");
        return;
      }

      if ((e.type === "blur" || isTriggerKey) && (value.length === 9 || value.length === 0)) {
        // setIsDoctorNumberDisabled(false);
        try {
          if (value.length === 9) {
            const clinicData = (await getClinicById(value)) as ClinicForModal;
            if (clinicData) {
              setDoesClinicExist(true);
              setCurrentClinic(clinicData);
              setFormData((prev) => ({ ...prev, clinicId: clinicData.id }));
              setShowClinicData(true);
            } else {
              // const inputValue = selectedBSNR || formData?.practiceNumber;
              setDoesClinicExist(false);
              setCurrentClinicId(value);
              setCurrentDoctorId(currentDoctor?.id || "");
              setShowClinicData(false);
              setCurrentClinic(undefined);
              setIsClinicModalOpen(true);
            }
          } else if (value.length === 0) {
            setCurrentClinic(undefined);
          }
        } catch (error) {
          console.error("Error fetching clinic:", error);
        }
      }
    };

    const handleGetDoctorById = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("LANR muss 9-stellig sein", "error");
        return;
      }

      if ((e.type === "blur" || isTriggerKey) && (value.length === 9 || value.length === 0)) {
        try {
          if (value.length === 9) {
            const doctorData: any = await getDoctorById(value);
            // console.log("doctor DATA -------> ", doctorData);
            if (doctorData) {
              setDoesDoctorExist(true);
              setCurrentDoctor(doctorData);
              setFormData((prev) => ({ ...prev, doctorId: doctorData.id }));
              // Only check association if currentClinic is set and valid
              if (currentClinic && currentClinic.id) {
                const isDoctorInClinic = doctorData.clinics?.some(
                  (clinic: any) => clinic.id === currentClinic.id
                );
                if (isDoctorInClinic) {
                  notificationStore.showMessage(
                    "This doctor exists and is already associated with this clinic",
                    "success"
                  );
                } else {
                  const confirmed = await confirmDoctorClinicJoin(doctorData, currentClinic);
                  if (!confirmed) {
                    // User cancelled — clear the doctor selection (treat as a mistake).
                    setCurrentDoctor(undefined);
                    setDoesDoctorExist(false);
                    setFormData((prev) => ({ ...prev, doctorId: "", doctorNumber: "" }));
                    return;
                  }
                  notificationStore.showMessage(
                    "This doctor exists already and will now get associated with this clinic",
                    "success"
                  );
                  handleAssociateDoctorToClinic(doctorData);
                }
              } else {
                // No clinic entered yet, just set doctor, do not associate
                notificationStore.showMessage(
                  "Bitte geben Sie zuerst eine gültige Betriebsstätten-Nr. (BSNR) ein.",
                  "warning"
                );
              }
            } else {
              setDoesDoctorExist(false);
              setIsClinicModalOpen(true);
              setCurrentDoctorId(value);
              setCurrentClinicId(currentClinic?.id || "");
            }
          } else if (value.length === 0) {
            setCurrentDoctor(undefined);
          }
        } catch (error) {}
      }
    };

    enum ReportMethod {
      Email = "Email",
      Fax = "Fax",
      Post = "Post",
    }

    const handleAssociateDoctorToClinic = async (doctorData: any) => {
      const createDoctorClinicDto = {
        id: doctorData?.id || "",
        contactInfo: {
          salutation: doctorData?.contactInfo?.salutation || "",
          firstNames: doctorData?.contactInfo?.firstNames || "",
          lastName: doctorData?.contactInfo?.lastName || "",
        },
        clinics: [
          {
            id: currentClinic?.id || "",
            contactInfo: {
              businessName: currentClinic?.contactInfo?.businessName || "",
              phone1: currentClinic?.contactInfo?.phone1 || "",
              fax: currentClinic?.contactInfo?.fax || "",
              addressLine1: currentClinic?.contactInfo?.addressLine1 || "",
              addressLine2: currentClinic?.contactInfo?.addressLine2 || "",
              city: currentClinic?.contactInfo?.city || "",
              zip: currentClinic?.contactInfo?.zip || "",
              email: currentClinic?.contactInfo?.email || "",
            },
            notes: currentClinic?.notes || "",
            methodForDrReport: (currentClinic?.methodForDrReport as ReportMethod) || "Email",
            methodForRxOrder: (currentClinic?.methodForRxOrder as ReportMethod) || "Email",
          },
        ],
      };

      // Call createDoctorWithClinic to save data
      // setLoading(true);
      try {
        const response = await createDoctorWithClinic(createDoctorClinicDto);
        return response;
      } catch (error) {
        console.error("Error saving doctor and clinic:", error);
      } finally {
        setLoading(false);
      }
    };

    const handleInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index?: any,
      idValue?: string
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, value, checked, type, id } = event.target;
      setUnsavedChanges(true);

      const inputValue = type === "checkbox" ? checked : value;

      if (name === "practiceNumber") {
        if (value.length > 9) return;
      }

      if (name === "doctorNumber") {
        if (value.length > 9) return;
        // Only update the field, do not clear doctor info here!
        setFormData((prevValues: any) => ({
          ...prevValues,
          doctorNumber: value,
        }));
        return;
      }

      if (name === "date" && formData.isPerpetual && value) {
        setFormData((prevValues: any) => ({
          ...prevValues,
          unvirtualize: true,
        }));
      }

      if (name === "therapy") {
        const updatedTherapyRxs = [...formData.therapyRxs];

        // MD-232: the line names the prescribed TERM as the text that was
        // typed, and the server resolves it text -> alias -> term on save.
        // Previously this looked the value up among tarif-bound BILLABLE LINE
        // ITEMS and wrote state only on a hit — so a real term like "KG-ZNS",
        // which is no line item's abbreviation, stored nothing at all and the
        // save reached the server naming no term ("Heilmittel unbekannt").
        // `abbreviation` is kept in step because the table cell reads it.
        // `isSupplementary` is preserved: it decides which row group the line
        // belongs to and is not a property of the typed text.
        if (value) {
          updatedTherapyRxs.forEach((therapy, i) => {
            if (therapy.id === idValue) {
              updatedTherapyRxs[i] = {
                ...updatedTherapyRxs[i],
                rawText: value,
                therapy: {
                  ...updatedTherapyRxs[i].therapy,
                  abbreviation: value,
                },
              };
            }
          });
        } else {
          // Cleared: drop the term as well as the display value.
          updatedTherapyRxs.forEach((therapy, i) => {
            if (therapy.id === idValue) {
              updatedTherapyRxs[i] = {
                ...updatedTherapyRxs[i],
                rawText: null,
                therapy: {
                  ...updatedTherapyRxs[i].therapy,
                  id: "",
                  abbreviation: "",
                  description: "",
                  price: "",
                },
              };
            }
          });
        }

        // Update form data with the new therapy data
        setFormData((prevFormData: any) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      // Check if the input name is "amount"
      if (name === "amount") {
        // Update therapyRxs based on rowIndex and therapyId
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
          if (therapy.id === idValue) {
            return {
              ...therapy,
              amount: value, // Update the amount property with the new value
              frequency: formData.therapyRxs[0].frequency || therapy.frequency,
            };
          }
          return therapy;
        });

        // Update the formData state with the updated therapyRxs
        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name === "rxSeries") {
        // Update therapyRxs based on rowIndex and therapyId
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
          if (therapy.id === idValue) {
            return {
              ...therapy,
              rxSeries: { id: id, label: value },
            };
          }
          return therapy;
        });

        // Update the formData state with the updated therapyRxs
        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      if (name === "therapyFrequency") {
        // Update the frequency for the specific therapy row
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
          return {
            ...therapy,
            frequency: {
              id: value, // Update the frequency id
              text: value, // Update the frequency text
            },
          };
        });

        // Update the formData state with the updated therapyRxs
        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name.startsWith("icdCodeEntries")) {
        const inputElement = document.querySelector(`[name="${name}"]`) as
          | HTMLInputElement
          | HTMLTextAreaElement;
        const cursorPosition = inputElement?.selectionStart || 0;
        let updatedValue = value;
        let newCursorPosition = cursorPosition; // Initialize new cursor position

        // Check if the length is at least 4 and if a period needs to be inserted
        if (value.length >= 4) {
          // Check if the period already exists at the correct position
          if (!value.includes(".")) {
            // Insert a period after the third character if it's not already there
            updatedValue = value.slice(0, 3) + "." + value.slice(3);

            // Adjust cursor position if a period was inserted before the original cursor position
            if (cursorPosition > 3) {
              newCursorPosition = cursorPosition + 1; // Move cursor one position forward
            }
          }
        }

        // Update the form values with the correctly formatted value
        setFormData((prevValues: any) => ({
          ...prevValues,
          icdCodeEntries: prevValues.icdCodeEntries.map((icdEntry: any, i: number) => {
            return i === index
              ? { id: idValue || undefined, userText: updatedValue }
              : { id: icdEntry?.id, userText: icdEntry?.userText || "" };
          }),
        }));

        // Restore cursor position after the update
        setTimeout(() => {
          if (inputElement) {
            inputElement.selectionStart = inputElement.selectionEnd = newCursorPosition;
          }
        }, 0);
      }

      if (name === "rxtype") {
        // The Rezept-typ DECIDES the tarif group. Without this the group the Rx
        // was opened with survived the type change, so a type switched to
        // Privat here was still billed against the old group.
        const selectedRxTypeId = String(id ? id : inputValue);
        const { nextTarifGroupId, requiresChoice } = resolveTarifGroupForRxType(
          safeMasterTablesData,
          selectedRxTypeId,
          formData?.tarifGroupId,
          accountTherapyType
        );
        setFormData((prevValues) => ({
          ...prevValues,
          diagnosegroup: null, // Clear diagnosegroup when rxtype changes
          tarifGroupId: nextTarifGroupId,
        }));
        if (requiresChoice) {
          notificationStore.showMessage(
            "Bitte wählen Sie eine Tarifgruppe für den gewählten Rezept-Typ.",
            "warning"
          );
        }
        // The Diagnosegruppen follow formData.rxtype on their own (filteredDiagnoseGroups).
      }

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   // setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //       doctorData:selectedDoctor
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      // Leitsymptomatik: the a/b/c/d codes and the patientenindividuelle flag are
      // independent — a Verordnung can require both, so neither clears the other.
      // Update form fields based on their names
      setFormData((prevValues: any) => ({
        ...prevValues,
        [name]: id ? id : inputValue,
      }));

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      if (insurer[0]) {
        if (name === "Receiptpayer") {
          // Autopopulate doctor and bsnr based on LANR selection

          const selectedPayer: any = insurer[0].find(
            (option: any) => option.id === id || option.ik === value
          );
          // console.log("UZZHHHH", selectedPayer);
          if (selectedPayer) {
            setFormData((prevFormValues: any) => ({
              ...prevFormValues,
              chipcard: selectedPayer?.ik,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));
            setSelectedChipCard(selectedPayer.ik);
            // console.log("selectedPayer", selectedPayer.name);
          }
        }

        if (name === "chipcard") {
          const selectedPayer: any = insurer[0].find((option: any) => option.ik === value);
          // console.log("UZZHHHH", selectedPayer);
          if (selectedPayer) {
            setFormData((prevFormValues: any) => ({
              ...prevFormValues,
              Receiptpayer: selectedPayer.id,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));

            setSelectedPayer(selectedPayer.name);
            // console.log("selectedPayer", selectedPayer.name);
          }
        }
      }

      setFormData((prevFormData) => {
        const newFormData = { ...prevFormData, [name]: value };
        debounceValidation(newFormData);
        return newFormData;
      });
    };
    const handleChipCardDataEntry = (event: React.KeyboardEvent | React.FocusEvent) => {
      rxvalidate();
      const chipCardValue = (event.currentTarget as HTMLInputElement).value;
      if (
        (event as React.KeyboardEvent).key === "Enter" ||
        event.type === "blur" ||
        event.type === "mouseleave"
      ) {
        // Check if the chipcard number is valid
        const selectedPayer = insurer[0]?.find((option: any) => option.ik === chipCardValue);
        if (selectedPayer) {
          // If valid, autofill payer info
          setFormData((prevFormValues: any) => ({
            ...prevFormValues,
            Receiptpayer: selectedPayer?.id,
            // tarifGroupId: selectedPayer?.tarifGroup?.id,
          }));
          setSelectedPayer(selectedPayer.name);
          notificationStore.showMessage(
            `${selectedPayer?.id} ist ein IK für ${selectedPayer?.name}`,
            "success"
          );
        } else if (chipCardValue) {
          // Show error if chipcard is invalid
          notificationStore.showMessage(
            "Bitte geben Sie eine gültige Chipkartennummer ein.",
            "warning"
          );
          // Pass all form values with updated chipcard value for validation
        }
      }
    };
    useEffect(() => {
      rxvalidate();
    }, [formData]);

    const rxvalidate = (fieldValues = formData) => {
      let temp: any = { ...rxerrors };
      // Validate chipcard (if chipcard exists in fieldValues)
      if ("chipcard" in fieldValues) {
        const chipCardValue = fieldValues.chipcard;
        if (chipCardValue && !insurer[0]?.some((option: any) => option.ik === chipCardValue)) {
          temp.chipcard = "Ungültige Chipkartennummer";
        } else {
          temp.chipcard = ""; // Clear error if valid
        }
      }
      if ("accountId" in fieldValues) temp.accountId = fieldValues.accountId ? "" : "Pflichtfeld";
      if ("tarifGroupId" in fieldValues)
        temp.tarifGroupId = fieldValues.tarifGroupId ? "" : "Pflichtfeld";

      rxsetErrors({
        ...temp,
      });

      if (fieldValues == formData) return Object.values(temp).every((x) => x == "");
    };
    const { rxerrors, rxsetErrors } = rxuseForm(formData, true, rxvalidate);
    const [validationData, setValidationData] = useState<any>({});
    const debounceValidation = useCallback(
      debounce(async (currentFormData) => {
        const data = await getLiveValidation(currentFormData);
        setValidationData(data);
      }, 100),
      []
    );

    const handleGenericSubmit = async (event?: any) => {
      if (event) {
        setLoading(false);
        event.preventDefault(); // Prevent default form submission behavior
      }
      try {
        if (rxvalidate()) {
          // The doctor must actually be registered at the practice the Rx names:
          // the stamp takes its telephone number from the PRACTICE, so a
          // mismatched pair prints a stranger's number on a document that leaves
          // the building. Asked here rather than on a field, because the Arzt-Nr
          // check only fires when that field loses focus - correcting the BSNR
          // afterwards, or re-saving an Rx loaded from the database, slips past it.
          if (!(await confirmDoctorClinicMismatch(currentDoctor, currentClinic))) {
            return;
          }
          setLoading(true);
          const updatedRxValues: any = {
            ...formData,
            // The form holds "" for fields it has not been given a value for,
            // but the columns behind them are typed and nullable — `a`-`d` are
            // boolean, `date` is date — so Postgres rejects "" outright:
            // `invalid input syntax for type boolean: ""`. The Rx never saves
            // and the dialog closes, taking the entry with it.
            //
            // Empty is normal here, not an edge case: of 15,365 prescriptions
            // in production 4,030 carry no date and 3,737 no `a`. Planning
            // before the paper prescription arrives is the ordinary workflow.
            //
            // The Leitsymptom effect only rescues these when the chosen
            // Diagnosegruppe DISALLOWS the symptom (`... ? prevValues.a :
            // false`) — when it allows it, the "" is kept. So the fault needs
            // no diagnosis group at all to appear, and every one of these
            // fields has to be converted or the error simply moves to the next
            // one: a/b first, then the date.
            a: emptyToNull(formData.a),
            b: emptyToNull(formData.b),
            c: emptyToNull(formData.c),
            d: emptyToNull(formData.d),
            date: emptyToNull(formData.date),
            id: rxFormData?.id || "",
            rxNumber: rxFormData?.rxNumber || "",
            LANR: formData.doctorNumber || "",
            BSNR: formData.practiceNumber || "",
            // Preserve the existing fallback-to-patient behavior on save. Provenance
            // is tracked via resolveSourcedValue; explicit copy-from-patient handling
            // is owned by the transparency UI work.
            insureeNumber: insureeNumberField.value ?? "",
            insuranceStatus: insuranceStatusField.value ?? "",
            accountId:
              account[0].find((option: any) => option.name === formData.accountId)?.id ||
              formData.accountId,
            rxtype:
              rxType[0].find((option: any) => option.text === formData.rxtype)?.id ||
              formData.rxtype ||
              "",
            // The first catalog row with this code was the Kasse group for PS3/PS4,
            // so Ergo Blanko Rxs were saved — and validated — against it.
            //
            // The third field with the same fault as `a`-`d` and `date` above.
            // When the Rezept-typ leaves a real CHOICE of group the pre-fill
            // effect writes "" (see `defaultDiagnosisGroup` — "" is what the
            // Select needs to render empty), `findDiagnosisGroup("")` returns
            // undefined, and the fallback below hands that "" straight to a
            // foreign key column: `violates foreign key constraint ...`.
            //
            // Physio never hit it because a Physio Blankoverordnung has exactly
            // one group (EX) and is pre-filled; Ergo has three — SB1, PS3, PS4 —
            // and is not. That is the whole difference between the two.
            diagnosegroup:
              emptyToNull(
                findDiagnosisGroup(
                  formData.diagnosegroup,
                  filteredDiagnoseGroups,
                  diagnoseGroups?.[0]
                )?.id || formData.diagnosegroup
              ),

            Receiptpayer:
              insurer[0].find((option: any) => option.id === formData.chipcard)?.id ||
              formData.Receiptpayer,
            // therapyRxs:
            //   formData.therapyRxs
            //     ?.filter((therapy: any) => therapy.therapy.id !== "") // Filter out entries without therapy.therapyId or therapy.id
            //     .map((therapy: any) => ({

            //       frequency: therapy.frequency || "",

            //     })) || [],

            therapyRxs: (formData.therapyRxs || []) // Ensure formData.therapyRxs is an array or provide an empty array
              // MD-232: a row is real when it NAMES A TERM. Gating on
              // `therapy.id` dropped every freshly entered row (see rowNamesTerm).
              .filter(rowNamesTerm)
              .map((therapy: any) => {
                if (
                  !(therapy.frequency && therapy.frequency.id) ||
                  !rowNamesTerm(therapy) ||
                  !therapy.amount
                ) {
                  throw new Error(
                    "Bitte fügen Sie die Behandlung hinzu, um das Rezept zu speichern/bearbeiten (Therapie, Menge, Häufigkeit sind Pflichtfelder) und deaktivieren Sie auch die Zelle nach der Bearbeitung!"
                  );
                }
                const validTherapy = validateTherapy(
                  rowTermText(therapy),
                  safeMasterTablesData,
                  termOptions
                );
                const validFrequency = validateFrequency(
                  therapy.frequency.id,
                  safeMasterTablesData
                );
                if (!validTherapy || !validFrequency) {
                  throw new Error("Bitte geben Sie eine gültige Therapie und Häufigkeit ein.");
                }
                const insurer = safeMasterTablesData
                  .filter((obj: any) => "insurer" in obj)
                  .map((obj: any) => obj.insurer); // Extract the insurer array

                const payer =
                  insurer[0].find((option: any) => option.name === formData.Receiptpayer)?.id ||
                  formData.Receiptpayer;
                const insurerInfo = insurer[0]?.filter((obj: any) => obj.id === payer)[0]
                  ?.tarifGroup?.id; //To check with Nafees why is it used
                // const therapyName = therapy.therapy.abbreviation.toUpperCase();
                // console.log("TherapyName SEEEEEEEEEEEEEe", therapyName);
                const tarifTherapy = getTherapyId(
                  safeMasterTablesData,
                  formData.tarifGroupId,
                  therapy.therapy.abbreviation
                );

                return {
                  ...therapy,
                  therapy: {
                    id: tarifTherapy,
                    abbreviation: therapy.therapy.abbreviation,
                    isSupplementary: therapy.therapy?.isSupplementary,
                  },
                };
              }),
          };
          if (currentDoctor) {
            updatedRxValues.doctorId = (currentDoctor as any).id || formData.doctorId;
            updatedRxValues.LANR = (currentDoctor as any).LANR || formData.doctorNumber || "";
          } else {
            updatedRxValues.doctor = null;
            updatedRxValues.doctorId = null;
            updatedRxValues.doctorNumber = "";
            updatedRxValues.LANR = "";
          }
          if (currentClinic) {
            updatedRxValues.clinicId = currentClinic.id || formData.clinicId;
            updatedRxValues.BSNR = (currentClinic as any).BSNR || formData.practiceNumber || "";
          } else {
            updatedRxValues.clinic = null;
            updatedRxValues.clinicId = null;
            updatedRxValues.practiceNumber = "";
            updatedRxValues.BSNR = "";
          }

          setUnsavedChanges(false);
          setFormData(updatedRxValues);
          if (entryType === EntryType.newRx && handleNewFormData) {
            handleNewFormData(updatedRxValues);
          } else {
            patientSaveRxs({ patientId: patientId!, rxs: [updatedRxValues] });
             onCloseRx()
          }
          
          //  await searchPatient({ id: patientValues.id });
        } else {
          notificationStore.showMessage(
            "Bitte füllen Sie alle erforderlichen Felder aus",
            "warning"
          );
        }
      } catch (error: any) {
        console.error("Error saving Rx:", error);
        notificationStore.showMessage(
          error.message || "Fehler beim Speichern des Rezepts",
          "error",
          error
        );
      } finally {
        // Set loading back to false when the API call is complete
        setLoading(false);
       
      }
    };

    //   useEffect(() => {
    //   const inputValue = selectedBSNR || formData?.practiceNumber;
    //   const isEmpty = !inputValue || inputValue.trim() === "";

    //   if (isEmpty === false) {
    //      setIsDoctorNumberDisabled(false);
    //   }
    // }, [selectedBSNR, formData?.practiceNumber, currentClinic?.id]);

    const [therapyRxs, setTherapyRxs] = useState(formData.therapyRxs); // State to manage therapyRxs array
    const componentRef = useRef(null);
    const handleDuplicateRow = (
      index: number,
      event: React.ChangeEvent<HTMLInputElement>,
      idValue?: string
    ) => {
      const { value } = event.target;
      // console.log("handleDuplicateRow called", { index, value }, event);
      setUnsavedChanges(true);

      const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
        // Check if the current therapy matches the ID we're updating
        if (therapy.id === idValue) {
          // No even-amount gate: nothing splits the amount in half any more
          // (that was the old shadow-line model). Odd totals with doubling are
          // legal — e.g. 7 units at 2/day = 3 double days + 1 single.
          const updatedTherapy = {
            ...therapy,
            isDoubled: value as any,
            unitsPerDay: value ? 2 : 1,
            amount: therapy.amount,
          };

          // Check for duplicate therapies
          const isDuplicate = formData.therapyRxs.some((othertherapy, otherIndex) => {
            if (otherIndex !== index) {
              // Compare relevant properties
              return updatedTherapy.therapy.abbreviation === othertherapy.therapy.abbreviation;
            }
            return false;
          });

          if (isDuplicate) {
            notificationStore.showMessage(
              "Die Behandlung ist bereits beendet, so dass kein Duplikat zulässig ist.",
              "warning"
            );
            // Return the therapy as is to avoid changes
            return therapy;
          }

          // Return the updated therapy if all conditions are met
          return updatedTherapy;
        }

        // Return the therapy as is if the ID doesn't match
        return therapy;
      });

      // Update the formData state with the updated therapyRxs
      setFormData((prevFormData) => ({
        ...prevFormData,
        therapyRxs: updatedTherapyRxs,
      }));

    };


    // console.log("formData", formData, readOnlyView);
    const FormContent = (
      <Grid
        container
        direction={"row"}
        spacing={1}
        xs={12}
        sx={{ paddingTop: "0px", paddingLeft: "2px", border: "0px dashed blue" }}
      >
        {!readOnlyView ? (
          <Grid item xs={12} md={3} sx={{ border: "0px dashed green" }}>
            <FlatCard title={`Rezept ${rxFormData?.rxNumber || ""}`}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controls.Select
                    name="rxtype"
                    label="Rezept-typ"
                    value={formData?.rxtype}
                    onChange={handleInputChange}
                    options={rxType[0]?.map((option: any) => ({
                      label: option?.text.toString(),
                      value: option?.text.toString(),
                      id: option?.id.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    id={1}
                    name="accountId"
                    label="Mandant"
                    value={formData?.accountId}
                    onChange={handleInputChange}
                    options={accounts?.map((option: any) => ({
                      label: option?.name?.toString(),
                      value: option?.name?.toString(),
                      id: option?.id?.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                    error={rxerrors.accountId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="status"
                    label="Rezeptverbleib"
                    value={formData?.status}
                    onChange={handleInputChange}
                    options={[
                      {
                        label: "noch nicht erstellt",
                        value: "noch nicht erstellt",
                      },
                      { label: "beim Patient", value: "beim Patient" },
                      { label: "beim Therapeut", value: "beim Therapeut" },
                      { label: "vorhanden", value: "vorhanden" },
                    ]}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="tarifGroupId"
                    label="Tarif Group"
                    id={1}
                    value={formData?.tarifGroupId}
                    onChange={handleInputChange}
                    options={tarifGroupOptions.map((value: any) => ({
                      label: value.toString(),
                      value: value.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Checkbox
                    name="isPerpetual"
                    label="Dauerrezept"
                    value={formData?.isPerpetual}
                    onChange={handleInputChange}
                    disabled={readOnlyView}
                  />
                </Grid>
              </Grid>
            </FlatCard>

            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData.doctorNumber,
                signatureStatus: formData.signatureStatus,
                clinic: currentClinic,
              }}
              validationData={validationData}
              showSignatureCheck={!readOnlyView}
              readOnlyView={readOnlyView}
              onInputChange={handleInputChange}
              onEdit={() => {
                setCurrentClinicId(currentClinic?.id || "");
                setCurrentDoctorId(currentDoctor?.id || (currentDoctor as any)?.id || "");
                setIsClinicModalEditMode(true);
                setIsClinicModalOpen(true);
              }}
              onMigrationComplete={(type, newValue) => {
                if (type === "doctor") {
                  setFormData((prev) => ({ ...prev, doctorNumber: newValue }));
                  const doctorPK = (currentDoctor as any)?.id;
                  if (doctorPK) getDoctorById(doctorPK).then((doc) => { if (doc) setCurrentDoctor(doc as any); });
                } else {
                  setFormData((prev) => ({ ...prev, practiceNumber: newValue }));
                  const clinicPK = currentClinic?.id;
                  if (clinicPK) getClinicById(clinicPK).then((cli) => { if (cli) setCurrentClinic(cli as any); });
                }
              }}
            />

            <FlatCard title="Art der Besuch"></FlatCard>
            <FlatCard
              title=" Heilmittel nach Maßgabe des Kataloges"
              sx={{ width: "500px", marginTop: "80px" }}
            >
              {/* MD-514: the Ausfüllhilfe has to be REACHABLE once the tarif
                  group is set, not only at the instant the form opens. This
                  button was commented out, and re-picking the Rezept-typ only
                  clears the tarif group again — so after the dialog closed
                  there was no way back to it at all. Hidden for Ergo and for
                  an existing Rx, matching the dialog's own conditions. */}
              {!readOnlyView && !selectedRxId && accountTherapyType !== "Ergotherapie" && (
                <Tooltip
                  title={
                    formData?.tarifGroupId
                      ? "Ausfüllhilfe öffnen"
                      : "Erst eine Tarifgruppe wählen"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1, mr: 1 }}
                      disabled={!formData?.tarifGroupId}
                      onClick={() => setAutoFill(true)}
                    >
                      Ausfüllhilfe
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                sx={{ mb: 1 }}
                onClick={() => {
                  setFormData((prevFormData: any) => ({
                    ...prevFormData,
                    therapyRxs: [
                      ...prevFormData.therapyRxs,
                      {
                        id: uuidv4(),
                        therapy: {
                          id: "",
                          abbreviation: "",
                          description: "",
                          price: "",
                          isSupplementary: false,
                        },
                        amount: "",
                        frequency: { id: "1-5", text: "1-5" },
                        // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
                        rxSeries: { label: "" },
                      },
                    ],
                  }));
                }}
                disabled={readOnlyView}
              >
                Therapie hinzufügen
              </Button>
              {/* </Box> */}
              <Table
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #ccc",
                  background: "white",
                  paddingTop: "10px",
                }}
              >
                <TableBody sx={{ maxHeight: "50px", overflowY: "scroll" }}>
                  <TableCell>Aktionen</TableCell>
                  <TableCell>Heilmittel</TableCell>
                  <TableCell></TableCell>
                  <TableCell>Menge</TableCell>
                  <TableCell>Series</TableCell>
                  {formData?.therapyRxs
                    ?.filter((therapy) => !therapy.therapy.isSupplementary) // Exclude supplementary therapies
                    .map((therapy, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        // style={{ border: "1px solid red", height: "1px" }}
                      >
                        <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTherapy(therapy.id)}
                            disabled={readOnlyView} // Prevent deletion in read-only mode
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                          }}
                        >
                          <TherapyTermField
                            therapyType={accountTherapyType}
                            disabled={readOnlyView}
                            name="therapy"
                            label=""
                            value={therapy.therapy.abbreviation}
                            onChange={(event: any) =>
                              handleInputChange(event, rowIndex, therapy?.id)
                            }
                            // options={therapyOptions[0]
                            //   ?.filter((therapyObj: any) => therapyObj.duration)
                            //   ?.map((therapy: any) => ({
                            //     label: therapy?.abbreviation?.toString(),
                            //     value: therapy?.abbreviation?.toString(),
                            //   }))}
                            style={{
                              width: "100px",
                              marginTop: "0px",
                              marginBottom: "0px",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                        {/* <TableCell
                            sx={{
                              padding: "0px",
                              direction: "column",
                              height: "1px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px" }}>
                              {therapy.therapy.description}
                            </Typography>
                          </TableCell> */}
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "10px",
                            height: "1px",
                          }}
                        >
                          <Controls.Checkbox
                            name="doubled"
                            value={therapy.isDoubled ? therapy.isDoubled : false}
                            label={
                              <Typography sx={{ fontSize: "10px" }}>Doppelbehandlung</Typography>
                            }
                            disabled={!therapy.therapy.abbreviation || readOnlyView}
                            onChange={(event: any) => {
                              handleDuplicateRow(rowIndex, event, therapy.id);
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Input
                            name="amount"
                            disabled={readOnlyView}
                            label=""
                            value={therapy.amount}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              handleInputChange(event, rowIndex, therapy.id);
                            }} // Pass rowIndex to handleInputChange
                            style={{ width: "100%", margin: "0px" }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "80px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Select
                            name="rxSeries"
                            // label="Rezeptverbleib"
                            id={1}
                            style={{ width: "100%", margin: "0px" }}
                            value={therapy.rxSeries?.label}
                            onChange={(event: any) =>
                              handleInputChange(event, rowIndex, therapy.id)
                            }
                            options={savedSeriesForPatient.map((option: any) => ({
                              label: option.label,
                              value: option.label,
                              id: option.id,
                            }))}
                            onBlur={true}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              Ergänzendes Heilmittel
              <Table
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #ccc",
                  background: "white",
                }}
              >
                <TableBody sx={{ maxHeight: "20px", overflowY: "scroll" }}>
                  <TableCell>Aktionen</TableCell>
                  <TableCell>Heilmittel</TableCell>
                  <TableCell></TableCell>
                  <TableCell>Menge</TableCell>
                  {formData?.therapyRxs
                    ?.filter((therapy) => therapy.therapy.isSupplementary)
                    .map((therapy: any, supplementaryIndex) => (
                      <TableRow
                        key={therapy.id}
                        // style={{ border: "1px solid red", height: "1px" }}
                      >
                        <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTherapy(therapy.id)}
                            disabled={readOnlyView} // Prevent deletion in read-only mode
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                          }}
                        >
                          <TherapyTermField
                            therapyType={accountTherapyType}
                            isSupplementary
                            disabled={readOnlyView}
                            name="therapy"
                            value={therapy?.therapy?.abbreviation || ""} // Default empty if no therapy
                            onChange={(event: any) =>
                              handleInputChange(event, supplementaryIndex, therapy?.id)
                            }
                            style={{
                              width: "110px",
                              marginTop: "0px",
                              marginBottom: "0px",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                        {/* <TableCell
                                          sx={{
                                            padding: "0px",
                                            direction: "column",
                                            height: "1px",
                                          }}
                                        >
                                          <Typography sx={{ fontSize: "10px" }}>
                                            {therapy.therapy.description}
                                          </Typography>
                                        </TableCell> */}
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "10px",
                            height: "1px",
                          }}
                        >
                          <Controls.Checkbox
                            name="doubled"
                            value={therapy?.isDoubled || false}
                            label={
                              <Typography sx={{ fontSize: "10px" }}>Doppelbehandlung</Typography>
                            }
                            disabled={!therapy?.therapy?.abbreviation || readOnlyView}
                            onChange={(event: any) =>
                              handleDuplicateRow(supplementaryIndex, event, therapy?.id)
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Input
                            name="amount"
                            value={therapy?.amount || ""}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              handleInputChange(event, supplementaryIndex, therapy?.id)
                            }
                            disabled={readOnlyView}
                            style={{ width: "100%", margin: "0px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </FlatCard>

            {/* <Controls.Input
              name="practiceNumber"
              label="BSNR"
              value={formData?.practiceNumber}
              onChange={handleInputChange}
            /> */}
          </Grid>
        ) : (
          <Grid item xs={12} md={4} sx={{ border: "0px dashed green" }}>
            <FlatCard title="Allgemein" sx={{ marginBottom: "2px" }}>
              <Grid container spacing={0} justifyContent="space-between">
                {/* First Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" sx={{ fontSize: "0.8em" }}>
                    Rezept-Typ:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.rxtype || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>

                {/* Second Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Mandant:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.accountId || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem", padding: "0px" }}
                  />
                </Grid>

                {/* Third Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Rx Location:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={
                      formData.isHomeVisit === true
                        ? "Haus/Heim"
                        : formData.isHomeVisit === false
                        ? "Praxis"
                        : "-"
                    }
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="processingStatus"
                    label="Status"
                    onChange={updateRxProcessingStatus}
                    value={formData?.processingStatus || RxDtoProcessingStatusEnum.InBehandlung}
                    options={Object.values(RxDtoProcessingStatusEnum).map((value) => ({
                      value: value,
                      label: value,
                    }))}
                    noTriggerChange={true}
                    // style={{
                    //   width: "100%",
                    //   marginTop: "0px",
                    //   marginBottom: "0px",
                    //   fontSize: "10px",
                    // }}
                  />
                </Grid>
              </Grid>
            </FlatCard>

            {rxFormData && (
              <FlatCard 
                title="Validierung" 
                sxContent={{ 
                  p: 1, 
                  overflow: "hidden",
                  "&:last-child": { 
                    paddingBottom: 1 
                  }
                }}
              >
                <Box 
                  sx={{ 
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    margin: 0,
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  <ValidationCard
                    patientData={patientValues}
                    selectedRx={formData}
                    form={formType.SHORT}
                  />
                </Box>
              </FlatCard>
            )}

            <FlatCard
              title="Rezepthinweise"
              sxContent={{ p: 0.75 }}
              sx={{
                paddingTop: "5px",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              <Notes notes={rxNotes} setNotes={updateNote} maxVisibleNotes={3} noteKind="Rezepthinweis" />
            </FlatCard>

            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData?.doctorNumber,
                clinic: currentClinic,
              }}
              showSignatureCheck={false}
              readOnlyView={true}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", border: "0px solid red", borderColor: "transparent" }}
        >
          {" "}
          {/* transparent border color is a hack because i couldnt figure out any way to add top padding*/}
          <ScalableComponentSimple occupiedHeight={readOnlyView ? 0.8 : 1}>
            <Container
              sx={{ border: "0px solid yellow" }}
              className={classes.container}
              ref={componentRef}
            >
              {/* Small box */}
              <form onSubmit={handleGenericSubmit} className={classes.form}>
                <Grid container spacing={1}>
                  <Grid item sx={{ border: "0px solid green" }}>
                    <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                      {/* Checkboxes */}
                      {/* Checkbox on the left */}
                      <Grid item xs={1}>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -10,
                                top: -20,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              disabled={readOnlyView}
                              name="isCopaymentExempted"
                              value={formData?.isCopaymentExempted === true}
                              onChange={() =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  isCopaymentExempted: true,
                                }))
                              }
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 10,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Zuzah- lungs- frei
                              </span>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -10,
                                top: 10,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },

                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isCopaymentExempted"
                              value={formData?.isCopaymentExempted === false}
                              onChange={() =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  isCopaymentExempted: false,
                                }))
                              }
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 40,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Zuzah- lungs- pflicht
                              </span>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -10,
                                top: 40,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isAccident"
                              value={formData?.isAccident}
                              onChange={handleInputChange}
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 70,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                Unfall- folgen
                              </span>
                            </div>
                          </div>
                        </Grid>

                        <Grid item xs={3}>
                          <div style={{ position: "relative" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              style={{
                                position: "absolute",
                                zIndex: 2,
                                left: -10,
                                top: 70,
                                "&:not(.Mui-checked)": {
                                  // Define the styles for the unchecked state here
                                  color: "rgba(128, 128, 128, 0.2)",
                                },
                                "& .MuiSvgIcon-root": { fontSize: 50 }, // Adjust the size of the checkbox icon
                              }}
                              name="isBVG"
                              value={formData?.isBVG}
                              onChange={handleInputChange}
                            />
                            <div
                              style={{
                                position: "absolute",
                                zIndex: 3,
                                top: 100,
                                left: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none", // Make the label not clickable
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: attentionColors.immediateAttention,
                                  fontWeight: "bold",
                                }}
                              >
                                BVG
                              </span>
                            </div>
                          </div>
                        </Grid>
                      </Grid>

                      {/* Form content */}

                      <Grid item xs={6}>
                        <div className={classes.box}>
                          {/* <Grid container spacing={2}> */}

                          <Grid item xs={12}>
                            <Typography className={classes.typography}>
                              Krankenkasse bzw. Kostenträger
                            </Typography>
                            <Controls.Input
                              disabled
                              name="Receiptpayer"
                              label=""
                              value={selectedPayer || formData.Receiptpayer}
                              onChange={handleInputChange}
                              style={{
                                width: "100%",
                              }}
                            />

                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography className={classes.typography}>
                              Name, Vorname des Versicherten
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={4}>
                                <Controls.Input
                                  disabled
                                  name=""
                                  label=""
                                  value={patientLastName}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <Controls.Input
                                  disabled
                                  name=""
                                  label=""
                                  value={patientFirstName}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <Typography className={classes.typography}>geb . am</Typography>
                                <Controls.DatePicker
                                  name=""
                                  label=""
                                  value={patientDob}
                                  onChange={handleInputChange}
                                  disabled
                                  variant="standard"
                                  style={{
                                    width: "100%",
                                    padding: "0px",
                                    margin: "0px",
                                    fontSize: "10px",
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                      margin: 0,
                                      paddingRight: "0px",
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>
                                  Kostenträgerkennung
                                </Typography>
                                <div style={{ height: "30px" }}>
                                  <Controls.Input
                                    disabled={readOnlyView}
                                    name="chipcard"
                                    label=""
                                    value={selectedChipCard || formData?.chipcard}
                                    onChange={handleInputChange}
                                    onKeyDown={handleChipCardDataEntry}
                                    onBlur={handleChipCardDataEntry}
                                    // onMouseLeave={handleChipCardDataEntry}
                                    style={{
                                      width: "100%",
                                    }}
                                    error={rxerrors.chipcard}
                                  />
                                </div>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography className={classes.typography}>
                                  Versicherten-Nr.
                                </Typography>
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="insureeNumber"
                                  label=""
                                  value={insureeNumberField.value || ""}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                  error={getKvnrError(insureeNumberField.value)}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Status</Typography>
                                <Controls.Select
                                  disabled={readOnlyView}
                                  name="insuranceStatus"
                                  label=""
                                  value={insuranceStatusField.value || ""}
                                  onChange={handleInputChange}
                                  options={[
                                    { label: "10000", value: "10000" },
                                    { label: "30000", value: "30000" },
                                    { label: "50000", value: "50000" },
                                  ]}
                                  style={{
                                    width: "100%",
                                    marginTop: "0px",
                                    marginBottom: "0px",
                                    fontSize: "10px",
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>
                                  Betriebsstätten-Nr.
                                </Typography>
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="practiceNumber"
                                  label=""
                                  value={formData?.practiceNumber}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                  onBlur={handlePracticeNumberInteraction}
                                  onKeyDown={handlePracticeNumberInteraction}
                                />
                              </Grid>

                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Arzt-Nr.</Typography>
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="doctorNumber"
                                  label=""
                                  value={formData?.doctorNumber}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                  onBlur={handleGetDoctorById}
                                  onKeyDown={handleGetDoctorById}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Datum</Typography>
                                <ValidationTooltip
                                  validity={validationData?.initialDateVAlidation?.validity}
                                  message={validationData?.initialDateVAlidation?.validity}
                                  placement="right"
                                  sx={{ zIndex: 1300 }}
                                >
                                  <Controls.DatePicker
                                    disabled={readOnlyView}
                                    name="date"
                                    label=""
                                    value={formData?.date}
                                    onChange={handleInputChange}
                                    variant="standard"
                                    style={{
                                      width: "100%",
                                      padding: "0px",
                                      margin: "0px",
                                      fontSize: "10px",
                                      "& .MuiInputBase-input": {
                                        padding: 0,
                                        margin: 0,
                                        paddingRight: "0px",
                                      },
                                    }}
                                    formatDate="dd.MM.yy"
                                  />
                                </ValidationTooltip>
                              </Grid>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      {/* Checkboxes on the right */}
                      <Grid item xs={5} sx={{ paddingLeft: "95px !important" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            whiteSpace: "normal",
                            color: attentionColors.immediateAttention,
                          }}
                        >
                          Heilmittelverordnung 13
                        </Typography>
                        <Grid item xs={2}>
                          <Controls.Checkbox
                            disabled
                            name=""
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "normal",
                                  color: attentionColors.immediateAttention,
                                }}
                              >
                                Physiotherapie
                              </Typography>
                            }
                            value={accountTherapyType === "Physiotherapie"}
                            onChange={handleInputChange}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Controls.Checkbox
                            disabled
                            name=""
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "normal",
                                  color: attentionColors.immediateAttention,
                                }}
                              >
                                Podologische
                              </Typography>
                            }
                            value={false}
                            onChange={handleInputChange}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Controls.Checkbox
                            disabled
                            name=""
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "normal",
                                  color: attentionColors.immediateAttention,
                                }}
                              >
                                Stimm-,Sprech-
                              </Typography>
                            }
                            value={false}
                            onChange={handleInputChange}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Controls.Checkbox
                            disabled
                            name=""
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "normal",
                                  color: attentionColors.immediateAttention,
                                }}
                              >
                                Ergotherapie
                              </Typography>
                            }
                            value={accountTherapyType === "Ergotherapie"}
                            onChange={handleInputChange}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ paddingTop: "20px" }}>
                      <Grid item xs={12}>
                        <ValidationTooltip
                          validity={validationData?.icdValidation?.validity}
                          message={validationData?.icdValidation?.validity}
                          placement="left"
                          sx={{ zIndex: 1300 }}
                        >
                          <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                            Behandlungsrelevante Diagnose(n)
                            <br />
                            ICD-10{" "}
                            <Button
                              onClick={handleAddInput}
                              sx={{ padding: 0, minWidth: "20px", height: "12px" }}
                              disabled={readOnlyView}
                            >
                              <Add
                                sx={{
                                  width: "15px",
                                  color: attentionColors.immediateAttention,
                                }}
                              />
                            </Button>
                          </Typography>
                        </ValidationTooltip>
                      </Grid>
                      <Grid item xs={3}>
                        <Box
                          sx={{
                            height: "75px",
                            width: "90%",
                            border: "1px solid lightgray",
                            overflowY: "scroll",
                            overflowX: "hidden",
                            padding: "3px",
                            backgroundColor: "white",
                          }}
                        >
                          {formData.icdCodeEntries.map((icdEntry: any, index: any) => (
                            <div key={`icd-${index}`}>
                              <Controls.Input
                                disabled={readOnlyView}
                                label=""
                                name={`icdCodeEntries-${index}`}
                                value={icdEntry.userText}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  handleInputChange(e, index, icdEntry?.id);
                                }}
                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                  if (e.key === "Tab" || e.key === "Enter") {
                                    e.preventDefault();
                                    // handleICDCodeEntrySubmit(formValues.icdCodeEntries[index], index);
                                    // handleICDCodeEntrySubmit(icdEntry, index);
                                    setCurrentICD(icdEntry.userText);
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Controls.TextArea
                          disabled={readOnlyView}
                          name="diagnosisText"
                          rows={3}
                          placeholder=""
                          value={formData?.diagnosisText}
                          onChange={handleInputChange}
                          style={{ flex: 1, height: "75px" }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ paddingTop: "5px" }}>
                      <Grid item xs={12} sx={{ paddingTop: "10px" }}>
                        <Grid container>
                          <Grid item xs={1}>
                            <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                              Diagnose gruppe
                            </Typography>
                          </Grid>
                          <Grid item xs={1}>
                            <div
                              style={{
                                position: "absolute",
                                width: "0.6in",
                                height: "0.3in",
                                backgroundColor: "white",
                              }}
                            >
                              <Controls.Select
                                disabled={readOnlyView}
                                name="diagnosegroup"
                                label=""
                                // id={1}
                                value={formData?.diagnosegroup}
                                onChange={handleInputChange}
                                options={filteredDiagnoseGroups?.map((option: any) => ({
                                  label: option?.code.toString(),
                                  value: option?.code.toString(),
                                  id: option?.id.toString(),
                                }))}
                                style={{
                                  width: "100%",
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  fontSize: "10px",
                                }}
                              />
                            </div>
                          </Grid>
                          <Grid item xs={2} sx={{ paddingLeft: "12px", paddingTop: "1px" }}>
                            <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                              Leitsymptomatik
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              gemäß Heilmittelkatalog
                            </Typography>
                          </Grid>
                          <Grid item xs={5} sx={{ paddingLeft: "15px" }}>
                            <Controls.Checkbox
                              name="a"
                              label="a"
                              value={formData?.a}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.a || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="b"
                              label="b"
                              value={formData?.b}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.b || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="c"
                              label="c"
                              value={formData?.c}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.c || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="d"
                              label="d"
                              value={formData?.d}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.d || readOnlyView}
                            />
                          </Grid>
                          <Grid item xs={3} sx={{ paddingLeft: "3px" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="hasIndividualSymptoms"
                              label={
                                <Typography sx={{ fontSize: "10px" }}>
                                  Patientenindividuelle Leitsymptomatik
                                </Typography>
                              }
                              value={formData?.hasIndividualSymptoms}
                              onChange={handleInputChange}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={9}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: attentionColors.immediateAttention,
                        }}
                      >
                        Leitsymptomatik (patientenindividuelle Leitsymptomatik als Freitext angeben)
                      </Typography>
                      <Controls.TextArea
                        name="symptoms"
                        disabled={readOnlyView}
                        rows={3}
                        placeholder=""
                        value={formData?.symptoms}
                        onChange={handleInputChange}
                        style={{ flex: 1, height: "50px " }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ValidationTooltip
                        validity={validationData?.therapyValidations?.validity}
                        message={validationData?.therapyValidations?.validity}
                        placement="left"
                        sx={{ zIndex: 1300 }}
                      >
                        <Typography className={classes.typography}>
                          Heilmittel nach Maßgabe des Kataloges
                        </Typography>
                      </ValidationTooltip>
                      <Grid item xs={12}>
                        {/* A real Blankoverordnung prescribes no concrete Heilmittel — the
                            field on the printed form carries the word itself. Prescription
                            lines are managed in the therapy table OUTSIDE this paper twin;
                            the twin deliberately shows none of them. */}
                        <Box
                          sx={{
                            border: "1px solid red",
                            background: "white",
                            minHeight: "56px",
                            padding: "2px 4px",
                          }}
                        >
                          <Typography sx={{ fontSize: "12px", fontWeight: "bold", letterSpacing: "0.08em" }}>
                            BLANKOVERORDNUNG
                          </Typography>
                        </Box>
                      </Grid>

                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: attentionColors.immediateAttention,
                        }}
                      >
                        Ergänzendes Heilmettel
                      </Typography>
                      <Grid item xs={12}>
                        <Box sx={{ border: "1px solid red", background: "white", minHeight: "24px" }} />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={3}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="therapyReport"
                              label={
                                <Typography className={classes.typography}>
                                  {" "}
                                  Therapiebericht
                                </Typography>
                              }
                              value={formData?.therapyReport}
                              onChange={handleInputChange}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Box display="flex" alignItems="center">
                              <Typography
                                variant="body1"
                                className={classes.typography}
                                sx={{ marginRight: "8px" }}
                              >
                                Hausbesuch
                              </Typography>
                              <FormControlLabel
                                label=""
                                control={
                                  <>
                                    <Checkbox
                                      checked={formData.isHomeVisit === true}
                                      onChange={handleCheckboxChange}
                                      disabled={readOnlyView}
                                      name="visitTypeCheckboxJa"
                                      value="yes"
                                    />
                                    <Typography variant="body1">ja</Typography>
                                    <Checkbox
                                      checked={formData.isHomeVisit === false}
                                      onChange={handleCheckboxChange}
                                      disabled={readOnlyView}
                                      name="visitTypeCheckboxNein"
                                      value="no"
                                    />
                                    <Typography variant="body1">nein</Typography>
                                  </>
                                }
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={4}>
                            <ValidationTooltip
                              validity={validationData?.therapyFrequencyValidation?.validity}
                              message={validationData?.therapyFrequencyValidation?.validity}
                              placement="right"
                              sx={{ zIndex: 1300 }}
                            >
                              <Grid container>
                                <Grid item xs={4} sx={{ paddingTop: "2px" }}>
                                  <Typography className={classes.typography}>
                                    Therapie-frequenz
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={6}
                                  sx={{
                                    background: "white",
                                    paddingTop: "4px",
                                    borderBottom: `1px solid ${attentionColors.immediateAttention}`,
                                  }}
                                >
                                  <Controls.Input
                                    disabled={readOnlyView}
                                    name="therapyFrequency"
                                    label=""
                                    value={
                                      Array.from(
                                        new Set(
                                          formData.therapyRxs.map(
                                            (therapy: any) => therapy.frequency?.id
                                          )
                                        )
                                      )[0]
                                    }
                                    onChange={handleInputChange}
                                    style={{ width: "100%", margin: "0px" }}
                                  />
                                </Grid>
                              </Grid>
                            </ValidationTooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="urgent"
                              label={
                                <Typography className={classes.typography}>
                                  Dringlicher Behandlungsbedarf innerhalb von 14 Tagen
                                </Typography>
                              }
                              value={formData?.urgent}
                              onChange={handleInputChange}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      {/* <Grid item xs={12}> */}
                      <Grid container>
                        <Grid
                          item
                          xs={7}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            paddingTop: "5px",
                          }}
                        >
                          <Controls.TextArea
                            label={
                              <Typography className={classes.typography}>
                                ggf. Thrapieziele/weitre med. Befunde und Himweise
                              </Typography>
                            }
                            disabled={readOnlyView}
                            name="additionalNotes"
                            rows={4}
                            placeholder=""
                            value={formData?.additionalNotes}
                            onChange={handleInputChange}
                            style={{ flex: 1, height: "100px " }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={5}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            paddingLeft: "20px",
                          }}
                        >
                          {/* <TextArea
                rows={7}
                cols={100}
                value={`${formData.doctorData?.contactInfo?.salutation} ${formData.doctorData?.contactInfo?.firstNames} ${formData.doctorData?.contactInfo?.lastName}\n` +
                  `${formData.doctorData?.contactInfo?.addressLine1}\n` +
                  `${formData.doctorData?.contactInfo?.addressLine2}\n` +
                  `Tel: ${formData.doctorData?.contactInfo?.tel}\n` + // Added "Tel:" label
                  `BSNR: ${formData.doctorData?.clinicNumber}` // Added "BSNR:" label
                }
                onChange={handleInputChange}
                sx={{ width: '50px', textAlign: 'justify', maxHeight: "80px", overflowY: "scroll" }} /> */}
                          <Controls.TextArea
                            name="doctorData"
                            rows={5}
                            placeholder=""
                            value={
                              `${currentDoctor?.contactInfo?.salutation || ""} ${
                                currentDoctor?.contactInfo?.firstNames || ""
                              } ${currentDoctor?.contactInfo?.lastName || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine1 || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine2 || ""}\n` +
                              `Tel: ${currentClinic?.contactInfo?.phone1 || ""}\n` + // Added "Tel:" label
                              `BSNR: ${currentClinic?.BSNR || ""}`
                            }
                            onChange={handleInputChange}
                            disabled
                            style={{ flex: 1, height: "80px " }}
                          />
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography className={classes.typography}>
                              IK des Leitungserbringers
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Box
                              sx={{
                                background: "white",
                                width: "100px",
                                height: "20px",
                              }}
                            ></Box>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* </Grid> */}
                    </Grid>
                  </Grid>
                </Grid>
              </form>
              <Modal
                open={loading}
                aria-labelledby="loading-modal-title"
                aria-describedby="loading-modal-description"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <CircularProgress size={60} />
                </div>
              </Modal>
            </Container>
          </ScalableComponentSimple>
        </Grid>
        {!readOnlyView && (
          <Grid item xs={12} md={3} sx={{ border: "0px dotted black" }}>
            <FlatCard title="Validierung">
              <ValidationCard
                patientData={patientValues}
                selectedRx={
                  formData
                  // patientValues?.rxs?.filter(
                  //   (rx: any) => rx.rxNumber === rxFormData?.rxNumber
                  // )[0]
                }
                form={formType.LONG}
              />
            </FlatCard>
          </Grid>
        )}
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}

        {/* MD-328: Physiotherapie only.
          *
          * Every template below prescribes PD, BD, MT, KG or KGG — positions of
          * the Physio § 125a catalog. None of them exists under ErgoBlanko,
          * whose Heilmittel are MFB, HLT and PFB, so on an Ergo practice these
          * buttons would fill the Rx with lines that resolve to no billable
          * position at all.
          *
          * Not hypothetical: `autoFill` defaults to true and the button that
          * used to toggle it is commented out, so this dialog opens by itself
          * on every new Blankoverordnung.
          *
          * Hidden rather than translated: the Ergo equivalents ("how many
          * Einheiten MFB for SB1") are a clinical judgement, not something
          * derivable from the Vertrag. Until someone who does Ergo supplies
          * them, no templates beats wrong ones — the Heilmittel dropdowns are
          * already filtered by discipline and work as they are.
          */}
        {!readOnlyView && !selectedRxId && accountTherapyType !== "Ergotherapie" && (
          <Dialog open={autoFill} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>PreFill Therapien</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                {/* Praxis Column */}
                <Grid item xs={6} style={{ textAlign: "center" }}>
                  <Box sx={styles.mainHeader}>Praxis</Box>
                  <Box sx={styles.arrow}>▼</Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={styles.subHeader}>Kein Bruch</Box>
                      <Box sx={styles.arrow}>▼</Box>
                      <Button style={styles.button} onClick={() => handleButtonClick("Kein Bruch")}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography>5 MT</Typography>
                          <Typography>5 KG</Typography>
                          <Typography>8 KGG</Typography>
                        </Box>
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={styles.subHeader}>Bruch</Box>
                      <Box sx={styles.arrow}>▼</Box>
                      <Button sx={styles.button} onClick={() => handleButtonClick("Bruch")}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography>8 MT</Typography>
                          <Typography>8 KG</Typography>
                          <Typography>10 KGG</Typography>
                        </Box>
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Home Visit Column */}
                <Grid item xs={6} sx={{ textAlign: "center" }}>
                  <Box sx={styles.mainHeader}>Haus/ Heim</Box>
                  <Box sx={styles.arrow}>▼</Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={styles.subHeader}>Der Therapeut hat MT</Box>
                      <Box sx={styles.arrow}>▼</Box>
                      <Button
                        sx={styles.button}
                        onClick={() => handleButtonClick("Therapeut hat MT")}
                      >
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography>10 MT</Typography>
                          <Typography>8 KG</Typography>
                        </Box>
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={styles.subHeader}>Kein MT</Box>
                      <Box sx={styles.arrow}>▼</Box>
                      <Button sx={styles.button} onClick={() => handleButtonClick("Kein MT")}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography>18 KG</Typography>
                        </Box>
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Abbrechen
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Grid>
    );

    // if (!selectedRxId || !entryType) {
    //   return (
    //     <Box p={2}>
    //       <span>Kein Rezept ausgewählt</span>
    //     </Box>
    //   );
    // }

    if (isRxLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (rxError) {
      return (
        <Box p={2}>
          <span>Fehler beim Laden des Rezepts für {selectedRxId}</span>
        </Box>
      );
    }
    return !readOnlyView ? (
      <Dialog
        open={openRx as boolean}
        onClose={handleCloseRxDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            left: position === "right-third" ? `${window.innerWidth * 0.33}px` : "auto",
            top: position === "right-third" ? "50%" : "auto",
            transform: position === "right-third" ? "translateY(-50%)" : "none",
            width: position === "right-third" ? `${window.innerWidth * 0.66}px` : "auto",
            height: position === "right-third" ? "90vh" : "auto",
            margin: position === "right-third" ? 0 : "auto",
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <span>{`${formData?.rxtype} Dialog`}</span>
          <Button onClick={() => setPosition(position === "default" ? "right-third" : "default")}>
            1
          </Button>
        </DialogTitle>
        <DialogContent>{FormContent}</DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseRxDialog}>
            Abbrechen
          </Button>
          <Button onClick={() => setDocumentViewerOpen(!documentViewerOpen)}>Dokumentviewer</Button>
          <Button
            onClick={handleGenericSubmit}
            variant="contained"
            disabled={Object.values(rxerrors).some((error) => error !== "")}
          >
            Speichern
          </Button>
        </DialogActions>
        <VerordnungDialog
          openAddEditDialog={isClinicModalOpen}
          handleCloseAddEditDialog={() => { setIsClinicModalOpen(false); setIsClinicModalEditMode(false); }}
          clinicId={currentClinicId}
          setCreationResponse={setCreationResponse}
          doctorId={currentDoctorId}
          currentClinic={currentClinic}
          currentDoctor={currentDoctor}
          initialDoctorName={autocompleteSearchTerm}
          isEditMode={isClinicModalEditMode}
        />
        <SimpleDocumentViewer
          open={documentViewerOpen}
          onClose={() => setDocumentViewerOpen(false)}
          position={position}
          patientId={patientValues?.id}
        />
      </Dialog>
    ) : (
      FormContent
    );
  }
);

export default Blankoverordnung;
